import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// eslint-disable-next-line import/no-webpack-loader-syntax
import mapboxgl from '!mapbox-gl';
import * as THREE from 'three';
import 'mapbox-gl/dist/mapbox-gl.css';
import { getDroneMarkerSVG, getEffectiveHeading } from '../utils/icons';
import { intentMarkerColor } from '../utils/intentState';
import { API_URL, apiFetch } from '../config/api';
import { getCachedOpsConfig, loadOpsConfig, OPS_CONFIG_EVENT } from '../config/opsConfig';
import { currentStage } from '../utils/missionStage';
import { layerOf, layerColor } from '../utils/altitudeLayers';
import { flownPath, legPhases, ADDED_POINT_TYPES, NON_LOCATION_TYPES } from '../utils/flightPath';
import { onDroneFocus } from '../utils/voiceLiveBus';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || '';
const DRONE_MODEL_URL = '/models/Drone.glb';
const DRONE_MODEL_LAYER_ID = 'utms-drone-model-layer';
const DRONE_MODEL_SCALE_METERS = 3.2;
// Keep the model readable from any camera distance: never let it subtend less
// than this fraction of the camera→drone distance (real scale wins close-up).
const DRONE_MIN_APPARENT_SCALE = 0.042;
const DRONE_ALTITUDE_OFFSET_METERS = 0.1;
const DRONE_HEADING_OFFSET_DEG = 0;
const DRONE_GROUND_CLEARANCE_METERS = 0.1;
const DRONE_GROUND_CLEARANCE_WITH_BUILDINGS_METERS = 0.2;
const DRONE_BUILDING_TOP_CLEARANCE_METERS = 10;
const DRONE_GROUND_LOCK_THRESHOLD_METERS = 0.35;
// Per-frame smoothing of the rendered drone pose (telemetry arrives ~2 Hz and
// snapping to each fix made the model move like a particle).
const DRONE_POS_LERP = 0.08;
const DRONE_HEADING_LERP = 0.10;
const DRONE_ATTITUDE_LERP = 0.12;
const DRONE_SNAP_DISTANCE_METERS = 300;  // teleport instead of gliding huge jumps
const DRONE_MAX_TILT_RAD = 0.6;          // clamp FC pitch/roll so bad data can't flip the model
// The helicopter GLB ships a "Take 001" clip that spins its rotors, but the loader
// keeps only gltf.scene and drops the animations — so we spin the rotor nodes
// ourselves each frame. Axes verified from the clip: main rotor turns about local Y,
// tail rotor about local X. Speeds in radians/sec (rev/sec × 2π).
const HELI_MAIN_ROTOR_SPEED = 3 * 2 * Math.PI;   // ~3 rev/sec
const HELI_TAIL_ROTOR_SPEED = 5 * 2 * Math.PI;   // ~5 rev/sec
// A helicopter pitches nose-down into its direction of travel — that forward tilt is
// what produces horizontal thrust. Telemetry attitude is often flat (position-only
// tracks, or a bridge that drops pitch/roll), so we add a speed-proportional forward
// lean so the helicopter visibly noses into its motion instead of sliding dead-level.
// Gated to the helicopter via modelConfig.leansIntoTravel — the quad/hexa stay flat.
// leanDir is the sign of the body-frame lean (+1 dips the nose DOWN; the heading/yaw
// offset does NOT affect this — a yaw can't flip nose-up vs nose-down).
const FWD_LEAN_PER_MPS = 0.035;  // radians of lean added per m/s of ground speed
const FWD_LEAN_MAX_RAD = 0.45;   // cap the forward lean (~26°)
const MODEL_RETRY_BASE_DELAY_MS = 800;
const MODEL_RETRY_MAX_DELAY_MS = 8000;
const TERRAIN_QUERY_OPTIONS = { exaggerated: true };
// How often the route ribbon re-scans terrain under its waypoints to self-heal
// altitude as DEM tiles load. Throttled so we don't query terrain for every
// waypoint on every render frame (the drone loop repaints at 60fps).
const TERRAIN_RECHECK_INTERVAL_MS = 250;

const modelTemplateCache = new Map();
const modelPromiseCache = new Map();

// Map normalized vehicleModel keys to GLB URLs.
const VEHICLE_MODEL_URL_MAP = {
  default: DRONE_MODEL_URL,
  quadcopter: DRONE_MODEL_URL,
  hexacopter: '/models/hexacopter.glb',
  fixedwing: '/models/glider.glb',
  helicopter: '/models/helicopter.glb',
};

const MAPBOX_STYLE_URLS = {
  Streets: 'mapbox://styles/mapbox/streets-v11',
  Hybrid: 'mapbox://styles/mapbox/satellite-streets-v11',
  Terrain: 'mapbox://styles/mapbox/outdoors-v11',
  Dark: 'mapbox://styles/mapbox/dark-v11',
  Navigation: 'mapbox://styles/mapbox/navigation-day-v1',
  NavigationPreviewNight: 'mapbox://styles/mapbox/navigation-night-v1',
};

const NON_LOCATION_COMMANDS = NON_LOCATION_TYPES;
// Airspace-corridor palette, one colour per flight phase (utils/flightPath.js
// legPhases). Green, amber and pink are kept out of the altitude-layer palette
// (blue → violet, utils/altitudeLayers.js) so a phase never reads as a layer.
const ROUTE_CORRIDOR_COLOR = '#55b6f2';       // cruise (layers off, or a leg changing layer) — blue
const ROUTE_TAKEOFF_COLOR = '#34d399';        // climb at home — green
const ROUTE_LAND_COLOR = '#fbbf24';           // final descent (LAND, or home after RTL) — amber
const ROUTE_DROP_COLOR = '#f472b6';           // delivery drop: down to drop height and back up — pink
const ROUTE_FLOWN_TRAIL_COLOR = '#ffffff';    // where the drone has actually flown this mission
const ROUTE_CORRIDOR_EDGE_COLOR = '#dff1ff';  // near-white block edges + nodes
// Mission / corridor cards sit right of the Mapbox zoom stack, which App.css
// places past the floating sidebar (--map-left-inset); at left: 12 they were
// drawn under the sidebar and over the zoom buttons.
const CARDS_LEFT = 'calc(var(--map-left-inset, 16px) + 48px)';
const ROUTE_PATH_DASH_COLOR = '#ff5a5a';      // red dashed flight-path line inside the corridor
const ROUTE_TRANSITION_COLOR = '#f59e0b';      // amber-gold for mid-flight reroute transition tunnel
const ROUTE_TRANSITION_DASH_COLOR = '#fef08a'; // bright gold dashed flight line inside transition tunnel
const ROUTE_MAX_MISSIONS_PER_DRONE = 12;      // cap corridors drawn per drone
const ROUTE_HOME_MAX_DISTANCE_METERS = 10000; // ignore a home that's implausibly far from the mission (stale/wrong)
const MAX_TRAIL_POINTS = 500;
const MAX_FLOWN_TRAIL_POINTS = 3000;     // 3D flown trail per drone (one point per metre moved)

// Winch delivery animation: when a drone with an active delivery mission hovers
// over a drop point at its drop height, a rope + parcel is lowered, released to
// the ground, and the empty rope retracted — mirroring the DO_WINCH mission legs.
// ponytail: the trigger is a client-side proximity+altitude heuristic (no
// per-waypoint MISSION_CURRENT telemetry). Upgrade to a real mission-item-reached
// signal from the backend if the heuristic ever misfires.
// Fallback durations, for a mission that does not carry its own winch timing.
// A mission from the delivery generator does: see winchMs where drops are built.
const WINCH_CARRY_LENGTH_METERS = 1.2;  // rope length while cruising with the parcel attached (takeoff → drop)
const WINCH_OUT_MS = 2500;          // rope pay-out duration
const WINCH_HOLD_MS = 1500;         // hold at full extension before the parcel releases
const WINCH_IN_MS = 2500;           // empty-rope retract duration
const PARCEL_SIZE_METERS = 0.7;     // rendered parcel box edge
const ROPE_RADIUS_METERS = 0.035;   // winch cable thickness (rendered as a thin cylinder, not a 1px line)
const ROPE_COLOR = 0xe6ded0;        // off-white winch cord
const PARCEL_TAPE_COLOR = 0xd9c9a3; // packing tape strip

// Mission states in which a corridor is "live" (drawn bright + gets a countdown).
// Mirrors MISSION_RUNNING_STATES on the backend.
// Airborne = the drone is flying this mission. MUST match backend
// utils/missionStates.js AIRBORNE_STATES. The old set was ['taking_off',
// 'running'] only, so the moment a mission went 'paused', 'rtl_battery' or
// 'connection_lost' — a single telemetry gap is enough — the frontend declared
// it inactive, the corridor dimmed and the winch parcel vanished mid-flight.
const ACTIVE_MISSION_STATES = new Set([
  'taking_off', 'running', 'paused', 'rtl_battery', 'connection_lost', 'returning',
]);

// 3D corridor (true-altitude flight path drawn with Three.js)
const ROUTE_RIBBON_LAYER_ID = 'utms-route-ribbon-layer';
const ROUTE_TUNNEL_HALF_WIDTH_METERS = 10;      // corridor half width fallback — matches config maxHorizontalDeviationMeters (the enforced tolerance)
const ROUTE_TUNNEL_OPACITY_ACTIVE = 0.45;       // wall opacity for the flying (active) route
const ROUTE_TUNNEL_OPACITY_PREVIEW = 0.28;      // dimmer walls for saved/preview routes
// Points are drawn at their true height: the takeoff column starts on the
// ground, a delivery drop goes down to its real drop height, a landing ends on
// the ground. (A 17 m floor used to lift all of these into the air, so the
// drawn path was not where the drone flies and the drone sat below its tube.)
const ROUTE_MIN_DISPLAY_AGL_METERS = 0;
const ROUTE_WAYPOINT_SPHERE_RADIUS_METERS = 2.5; // marker sphere at each waypoint
const ROUTE_DASH_UNIT_METERS = 14;              // dash + gap length of the red path line
const ROUTE_DASH_MAX_PER_ROUTE = 1200;          // cap dash instances per route (long routes space out)
const ROUTE_DASH_CROSS_SECTION_METERS = 0.2;    // narrow cross-section in metres (was chunky 0.9m)

const THIRD_PERSON_BACK_DISTANCE_METERS = 18;
const THIRD_PERSON_UP_DISTANCE_METERS = 8;
const SECOND_PERSON_AHEAD_DISTANCE_METERS = 13;   // oncoming/hero shot: camera sits in front of the drone
const SECOND_PERSON_UP_DISTANCE_METERS = 5;       // ~21° down — drone framed centre, less top-down than before
const FIRST_PERSON_FORWARD_DISTANCE_METERS = 2;  // camera sits this far AHEAD of the drone so the ~1.5m airframe stays out of frame
const FIRST_PERSON_UP_DISTANCE_METERS = 0.4;     // roughly nose height above the drone origin
const FIRST_PERSON_LOOKAHEAD_METERS = 40;
const FIRST_PERSON_LOOK_DROP_METERS = 14;        // look point sits this far below flight alt over LOOKAHEAD (~19° down) so the view aims at the route/ground ahead, not the horizon
const MAV_FRAME_GLOBAL_ABSOLUTE_ALT = 0;
const DRONE_RADIUS_METERS = 10;
const DRONE_RADIUS_STEPS = 64;
const DRONE_RADIUS_SOURCE_ID = 'utms-drone-radius-src';
const DRONE_RADIUS_FILL_ID = 'utms-drone-radius-fill';
const DRONE_RADIUS_LINE_ID = 'utms-drone-radius-line';

// Zone overlay constants (matching /data/map.geojson properties)
const ZONE_SOURCE_ID = 'utms-zones-src';
const ZONE_FILL_LAYER_ID = 'utms-zones-fill';
const ZONE_LINE_LAYER_ID = 'utms-zones-line';
const ZONE_EXTRUSION_HEIGHT_METERS = 120; // Visible 3D column height for no-fly zones
const ZONE_GEOJSON_URL = '/data/map.geojson';

const OVERLAY_STYLES = {
  statusBanner: {
    position: 'absolute',
    top: 16,
    left: 'var(--map-left-inset, 16px)',
    zIndex: 20,
    padding: '8px 14px',
    borderRadius: 12,
    border: '1px solid rgba(0, 218, 243, 0.25)',
    background: 'rgba(13, 17, 23, 0.88)',
    backdropFilter: 'blur(16px) saturate(130%)',
    WebkitBackdropFilter: 'blur(16px) saturate(130%)',
    boxShadow: '0 8px 32px -4px rgba(0, 0, 0, 0.65)',
    color: 'var(--textPrimary, #ffffff)',
    fontSize: 12,
    fontWeight: 600,
    transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  errorBanner: {
    position: 'absolute',
    top: 16,
    left: 'var(--map-left-inset, 16px)',
    zIndex: 20,
    padding: '8px 14px',
    borderRadius: 12,
    border: '1px solid rgba(239, 68, 68, 0.45)',
    background: 'rgba(18, 12, 14, 0.88)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    boxShadow: '0 8px 32px -4px rgba(0, 0, 0, 0.65)',
    color: '#fca5a5',
    fontSize: 12,
    fontWeight: 600,
    transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  missingToken: {
    height: '100%',
    width: '100%',
    background: 'var(--bgPrimary)',
    color: 'var(--textPrimary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '24px',
  },
};

function cleanupMapLayers(map, prefix) {
  if (!map) return;
  try {
    const style = map.getStyle();
    if (!style) return;
    (style.layers || []).forEach((layer) => {
      if (layer.id.startsWith(prefix)) {
        map.removeLayer(layer.id);
      }
    });
    Object.keys(style.sources || {}).forEach((srcId) => {
      if (srcId.startsWith(prefix)) {
        map.removeSource(srcId);
      }
    });
  } catch { /* style may be unavailable during teardown */ }
}

function hasValidCoordinates(lat, lon) {
  return Number.isFinite(lat) && Number.isFinite(lon) && !(lat === 0 && lon === 0);
}

// Telemetry-only guard: raw MAVLink altitude fields (alt, relative_alt) can
// arrive in millimetres. This converts those to metres. NOTE: it is a magnitude
// heuristic, so do not use it on values known to be metres already (e.g. route
// waypoints) — a legitimate absolute-MSL altitude > 2000 m would be wrongly
// divided. Used for low-altitude telemetry where the ambiguity does not bite.
function normalizeAltitudeMeters(value) {
  if (!Number.isFinite(value)) return 0;
  if (Math.abs(value) > 2000) {
    return value / 1000;
  }
  return value;
}

function resolveLocalBuildingTopMeters(map, lat, lon, terrainMeters = 0) {
  if (!map?.getLayer || !map?.project || !map?.queryRenderedFeatures) {
    return null;
  }
  if (!map.getLayer('utms-3d-buildings')) {
    return null;
  }

  try {
    const point = map.project([lon, lat]);
    const features = map.queryRenderedFeatures(point, { layers: ['utms-3d-buildings'] });
    if (!features || features.length === 0) {
      return null;
    }

    let maxTopMsl = null;
    features.forEach((feature) => {
      const height = Number(feature?.properties?.height);
      const minHeight = Number(feature?.properties?.min_height);
      const topAgl = Math.max(
        Number.isFinite(height) ? height : 0,
        Number.isFinite(minHeight) ? minHeight : 0
      );
      const topMsl = terrainMeters + topAgl;
      maxTopMsl = Number.isFinite(maxTopMsl) ? Math.max(maxTopMsl, topMsl) : topMsl;
    });

    return maxTopMsl;
  } catch {
    return null;
  }
}

function ensureDroneLayerOnTop(map) {
  if (!map?.getLayer || !map?.moveLayer) {
    return;
  }
  if (map.getLayer(DRONE_MODEL_LAYER_ID)) {
    map.moveLayer(DRONE_MODEL_LAYER_ID);
  }
}

function getRouteInterpolatedGround(droneLat, droneLon, routePts) {
  if (!routePts || routePts.length === 0) {
    return { interpolatedGround: null, distance: Infinity };
  }

  // Project in local metres, not raw degrees. A degree of longitude is only
  // cos(lat) as long as a degree of latitude (~7% short at 21 N), so comparing
  // degree deltas directly skews where the closest point lands on the leg AND
  // inflates the distance the caller then thresholds on.
  const M_PER_DEG_LAT = 111320;
  const mPerDegLon = M_PER_DEG_LAT * Math.cos((droneLat * Math.PI) / 180);

  let minDistanceSq = Infinity;
  let closestGroundAlt = null;

  for (let i = 0; i < routePts.length - 1; i++) {
    const p1 = routePts[i];
    const p2 = routePts[i + 1];

    const dx = (p2.lon - p1.lon) * mPerDegLon;
    const dy = (p2.lat - p1.lat) * M_PER_DEG_LAT;
    const lenSq = dx * dx + dy * dy;

    // Offset of the drone from this leg's start, same metre space.
    const ox = (droneLon - p1.lon) * mPerDegLon;
    const oy = (droneLat - p1.lat) * M_PER_DEG_LAT;

    let t = 0;
    if (lenSq > 1e-6) { // 1 mm^2 — anything shorter is a duplicate waypoint
      t = (ox * dx + oy * dy) / lenSq;
      t = Math.max(0, Math.min(1, t));
    }

    const distDx = ox - t * dx;
    const distDy = oy - t * dy;
    const distSq = distDx * distDx + distDy * distDy;

    if (distSq < minDistanceSq) {
      minDistanceSq = distSq;

      const g1 = p1.groundAlt !== undefined ? p1.groundAlt : 0;
      const g2 = p2.groundAlt !== undefined ? p2.groundAlt : 0;
      closestGroundAlt = g1 + t * (g2 - g1);
    }
  }

  return { interpolatedGround: closestGroundAlt, distance: Math.sqrt(minDistanceSq) };
}

function resolveModelAltitudeMeters(map, pose, droneId = null, activeRoutesRef = null) {
  const has3DBuildings = !!map?.getLayer?.('utms-3d-buildings');
  const groundClearance = has3DBuildings
    ? DRONE_GROUND_CLEARANCE_WITH_BUILDINGS_METERS
    : DRONE_GROUND_CLEARANCE_METERS;
  const requestedAgl = Number.isFinite(pose.altitudeAgl) ? Math.max(0, pose.altitudeAgl) : null;

  const terrainElevation = map?.queryTerrainElevation
    ? map.queryTerrainElevation([pose.lon, pose.lat], TERRAIN_QUERY_OPTIONS)
    : null;
  const terrainMeters = Number.isFinite(terrainElevation) ? terrainElevation : null;

  // ── Interpolate ground elevation from active route if drone is close to it ──
  let groundAlt = Number.isFinite(terrainMeters) ? terrainMeters : 0;
  if (droneId && activeRoutesRef && activeRoutesRef.current) {
    const activeRoutePts = activeRoutesRef.current[droneId];
    if (activeRoutePts && activeRoutePts.length >= 2) {
      const { interpolatedGround, distance } = getRouteInterpolatedGround(pose.lat, pose.lon, activeRoutePts);
      if (interpolatedGround !== null && Number.isFinite(terrainMeters)) {
        if (distance < 20) {
          groundAlt = interpolatedGround;
        } else if (distance < 40) {
          // Smoothly blend between interpolated ground and raw local terrain
          const t = (distance - 20) / 20;
          groundAlt = (1 - t) * interpolatedGround + t * terrainMeters;
        }
      }
    }
  }

  let resolvedAltitude = Math.max(0, groundAlt + groundClearance);

  if (Number.isFinite(requestedAgl)) {
    if (requestedAgl <= DRONE_GROUND_LOCK_THRESHOLD_METERS) {
      resolvedAltitude = groundAlt + groundClearance;
    } else {
      resolvedAltitude = groundAlt + requestedAgl;
    }
  } else if (Number.isFinite(pose.altitudeMsl) && Number.isFinite(terrainMeters)) {
    if (pose.altitudeMsl < groundAlt - 5) {
      resolvedAltitude = Math.max(groundAlt + groundClearance, groundAlt + pose.altitudeMsl);
    } else {
      resolvedAltitude = Math.max(groundAlt + groundClearance, pose.altitudeMsl);
    }
  } else if (Number.isFinite(pose.altitudeMsl)) {
    resolvedAltitude = Math.max(0, pose.altitudeMsl);
  } else if (Number.isFinite(pose.altitudeAgl)) {
    resolvedAltitude = Math.max(0, pose.altitudeAgl);
  }

  if (Number.isFinite(pose.localBuildingTopMsl) && (!Number.isFinite(requestedAgl) || requestedAgl > 2)) {
    resolvedAltitude = Math.max(resolvedAltitude, pose.localBuildingTopMsl + DRONE_BUILDING_TOP_CLEARANCE_METERS);
  }

  return resolvedAltitude;
}

function hasCachedModelTemplate(modelUrl) {
  return modelTemplateCache.has(modelUrl);
}

function normalizeVehicleModelKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
}

function resolveVehicleModelUrl(vehicleModel) {
  if (!vehicleModel) return DRONE_MODEL_URL;
  const trimmed = String(vehicleModel).trim();
  if (!trimmed) return DRONE_MODEL_URL;

  if (trimmed.endsWith('.glb') && (trimmed.startsWith('/') || trimmed.startsWith('http'))) {
    return trimmed;
  }

  const key = normalizeVehicleModelKey(trimmed);
  return VEHICLE_MODEL_URL_MAP[key] || DRONE_MODEL_URL;
}

function resolveVehicleModelConfig(vehicleModel) {
  const trimmed = String(vehicleModel || '').trim();
  const key = normalizeVehicleModelKey(trimmed);
  const url = resolveVehicleModelUrl(vehicleModel);

  let scaleMultiplier = 1.25;
  let headingOffsetDeg = 0;
  // Multirotors & helicopters dynamically pitch into forward travel and bank into turns
  let leansIntoTravel = true;
  let banksIntoTurns = true;
  let leanDir = 1;

  if (key === 'hexacopter') {
    scaleMultiplier = 1.1;
  } else if (key === 'fixedwing' || key === 'glider') {
    scaleMultiplier = 0.9;
    leansIntoTravel = false;
  } else if (key === 'helicopter') {
    scaleMultiplier = 1.15;
    leansIntoTravel = true;
  }

  return { url, scaleMultiplier, headingOffsetDeg, leansIntoTravel, banksIntoTurns, leanDir };
}

// Locate the spinning rotor nodes inside a cloned helicopter model so they can be
// animated per frame. Returns [] for models without rotors (quads, fixed-wing).
function findRotorSpinners(model) {
  const rotors = [];
  model.traverse((obj) => {
    const name = (obj.name || '').toLowerCase();
    if (name.includes('propellar') || name.includes('propeller') || name.includes('mainrotor')) {
      rotors.push({ obj, axis: 'y', speed: HELI_MAIN_ROTOR_SPEED });
    } else if (name.includes('backrotor') || name.includes('tailrotor')) {
      rotors.push({ obj, axis: 'x', speed: HELI_TAIL_ROTOR_SPEED });
    }
  });
  return rotors;
}

// ─── Procedural THREE.js drone bodies (no GLB) ───
// Built Y-up with the nose toward +Z to match the model→mercator rotation
// pipeline (rotationUpright maps model Y to mercator up). Rotor children are
// Cached BERAM logo texture
let cachedBeramLogoTexture = null;
function getBeramLogoTexture() {
  if (!cachedBeramLogoTexture) {
    cachedBeramLogoTexture = new THREE.TextureLoader().load(`${process.env.PUBLIC_URL}/BERAMLOGO.png`);
    if (cachedBeramLogoTexture) {
      cachedBeramLogoTexture.colorSpace = THREE.SRGBColorSpace;
      cachedBeramLogoTexture.generateMipmaps = true;
    }
  }
  return cachedBeramLogoTexture;
}

// ─── High-Fidelity 3D Parcel Model with BERAM Brand on All 4 Sides ───
let cachedParcelSideTexture = null;
function getParcelSideTexture() {
  if (cachedParcelSideTexture) return cachedParcelSideTexture;

  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 512;
  const ctx = canvas.getContext('2d');

  function renderSide(logoImg) {
    // 1. Solid Kraft Cardboard Base
    ctx.fillStyle = '#b8824a';
    ctx.fillRect(0, 0, 512, 512);

    // Subtle cardboard horizontal ribbing
    ctx.fillStyle = 'rgba(0, 0, 0, 0.04)';
    for (let y = 0; y < 512; y += 8) {
      ctx.fillRect(0, y, 512, 4);
    }

    // Heavy-duty cardboard perimeter edge folds
    ctx.strokeStyle = '#7c4d1f';
    ctx.lineWidth = 14;
    ctx.strokeRect(7, 7, 498, 498);

    // Darker corner reinforcement brackets
    ctx.fillStyle = '#8f5b28';
    ctx.fillRect(10, 10, 48, 48);
    ctx.fillRect(454, 10, 48, 48);
    ctx.fillRect(10, 454, 48, 48);
    ctx.fillRect(454, 454, 48, 48);

    // Center vertical packing seam tape
    ctx.fillStyle = 'rgba(226, 178, 122, 0.9)';
    ctx.fillRect(238, 0, 36, 512);

    // Top Cargo Stamp Header
    ctx.fillStyle = '#1c1208';
    ctx.font = 'bold 22px monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('★ BERAM AIR CARGO ★', 256, 62);

    // 2. Large Central Circular BERAM Seal
    const cx = 256;
    const cy = 245;
    const r = 135;

    // Outer Glowing Cyan Halo Ring
    ctx.beginPath();
    ctx.arc(cx, cy, r + 10, 0, Math.PI * 2);
    ctx.fillStyle = '#00e5ff';
    ctx.shadowColor = '#00e5ff';
    ctx.shadowBlur = 18;
    ctx.fill();
    ctx.shadowBlur = 0;

    // Dark Stealth Composite Medallion
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = '#0f141a';
    ctx.fill();
    ctx.strokeStyle = '#00e5ff';
    ctx.lineWidth = 6;
    ctx.stroke();

    // Inner subtle ring
    ctx.beginPath();
    ctx.arc(cx, cy, r - 12, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.35)';
    ctx.lineWidth = 2;
    ctx.stroke();

    // 3. Draw BERAM Logo Image inside seal
    if (logoImg && logoImg.complete && logoImg.naturalWidth > 0) {
      const logoW = (r - 16) * 2;
      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, r - 16, 0, Math.PI * 2);
      ctx.clip();
      ctx.drawImage(logoImg, cx - logoW / 2, cy - logoW / 2, logoW, logoW);
      ctx.restore();
    } else {
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 46px Arial, sans-serif';
      ctx.fillText('BERAM', cx, cy - 8);
      ctx.fillStyle = '#00e5ff';
      ctx.font = 'bold 20px Arial, sans-serif';
      ctx.fillText('EXPRESS', cx, cy + 30);
    }

    // 4. Industrial Delivery Waybill Label at Bottom
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(80, 420, 352, 64);
    ctx.strokeStyle = '#334155';
    ctx.lineWidth = 2;
    ctx.strokeRect(80, 420, 352, 64);

    // Barcode stripes
    ctx.fillStyle = '#0f172a';
    for (let i = 0; i < 38; i++) {
      const bx = 96 + i * 8.6;
      const bw = (i % 3 === 0 || i % 7 === 0) ? 5 : 2.5;
      ctx.fillRect(bx, 428, bw, 32);
    }
    ctx.font = 'bold 12px monospace';
    ctx.fillStyle = '#0f172a';
    ctx.textAlign = 'center';
    ctx.fillText('BRM-UTMS-AIR-09  |  PRIORITY FLIGHT', 256, 474);
  }

  renderSide(null);

  cachedParcelSideTexture = new THREE.CanvasTexture(canvas);
  cachedParcelSideTexture.colorSpace = THREE.SRGBColorSpace;
  cachedParcelSideTexture.generateMipmaps = true;

  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    renderSide(img);
    cachedParcelSideTexture.needsUpdate = true;
  };
  img.src = `${process.env.PUBLIC_URL}/BERAMLOGO.png`;

  return cachedParcelSideTexture;
}

function createParcelBoxGeometry() {
  const geo = new THREE.BufferGeometry();
  const positions = [];
  const normals = [];
  const uvs = [];
  const indices = [];

  function addQuad(p0, p1, p2, p3, n) {
    const base = positions.length / 3;
    positions.push(
      p0[0], p0[1], p0[2],
      p1[0], p1[1], p1[2],
      p2[0], p2[1], p2[2],
      p3[0], p3[1], p3[2]
    );
    for (let i = 0; i < 4; i++) {
      normals.push(n[0], n[1], n[2]);
    }
    uvs.push(0, 0, 1, 0, 1, 1, 0, 1);
    indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
  }

  // 1. Front (+Y): normal (0, 1, 0)
  addQuad(
    [-0.5, 0.5, -0.5],
    [ 0.5, 0.5, -0.5],
    [ 0.5, 0.5,  0.5],
    [-0.5, 0.5,  0.5],
    [0, 1, 0]
  );

  // 2. Back (-Y): normal (0, -1, 0)
  addQuad(
    [ 0.5, -0.5, -0.5],
    [-0.5, -0.5, -0.5],
    [-0.5, -0.5,  0.5],
    [ 0.5, -0.5,  0.5],
    [0, -1, 0]
  );

  // 3. Right (+X): normal (1, 0, 0)
  addQuad(
    [0.5,  0.5, -0.5],
    [0.5, -0.5, -0.5],
    [0.5, -0.5,  0.5],
    [0.5,  0.5,  0.5],
    [1, 0, 0]
  );

  // 4. Left (-X): normal (-1, 0, 0)
  addQuad(
    [-0.5, -0.5, -0.5],
    [-0.5,  0.5, -0.5],
    [-0.5,  0.5,  0.5],
    [-0.5, -0.5,  0.5],
    [-1, 0, 0]
  );

  // 5. Top (+Z): normal (0, 0, 1)
  addQuad(
    [-0.5, -0.5, 0.5],
    [ 0.5, -0.5, 0.5],
    [ 0.5,  0.5, 0.5],
    [-0.5,  0.5, 0.5],
    [0, 0, 1]
  );

  // 6. Bottom (-Z): normal (0, 0, -1)
  addQuad(
    [-0.5,  0.5, -0.5],
    [ 0.5,  0.5, -0.5],
    [ 0.5, -0.5, -0.5],
    [-0.5, -0.5, -0.5],
    [0, 0, -1]
  );

  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geo.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  geo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geo.setIndex(indices);

  // Group 0: 4 vertical sides = 4 * 6 = 24 indices -> Material 0 (Branded Sides)
  geo.addGroup(0, 24, 0);
  // Group 1: Top & Bottom = 2 * 6 = 12 indices -> Material 1 (Kraft Top/Bottom)
  geo.addGroup(24, 12, 1);

  return geo;
}

// ─── Procedural THREE.js drone bodies (Enterprise Aerospace Delivery Drone) ───
// High-tech industrial design: Sleek carbon composite fuselage, forward 4K gimbal & LiDAR pod,
// circular BERAM logo medallion on top deck, aviation navigation strobes (Red port, Green starboard, White tail),
// carbon-fiber tubular arms, high-performance aerodynamic folding blades, and aero landing skids.
// Built Y-up with nose toward +Z to match model→mercator orientation pipeline.
function buildMultirotorMesh(arms = 4) {
  const group = new THREE.Group();

  // ── Enterprise Aerospace Palette ──
  const carbonMat = new THREE.MeshStandardMaterial({
    color: 0x181b20,
    roughness: 0.38,
    metalness: 0.72,
  }); // Stealth Carbon Fiber / Dark Graphite
  const canopyMat = new THREE.MeshStandardMaterial({
    color: 0x22272e,
    roughness: 0.32,
    metalness: 0.65,
  }); // Sleek Gunmetal Top Shell
  const titaniumMat = new THREE.MeshStandardMaterial({
    color: 0x3d4450,
    roughness: 0.22,
    metalness: 0.88,
  }); // CNC Anodized Titanium & Motor Bell
  const glassSensorMat = new THREE.MeshStandardMaterial({
    color: 0x07090d,
    roughness: 0.08,
    metalness: 0.95,
  }); // Dark Optical Glass (Sensors/FPV)
  const redNavMat = new THREE.MeshStandardMaterial({
    color: 0xff1744,
    emissive: 0xff1744,
    emissiveIntensity: 2.4,
    roughness: 0.2,
  }); // Port (Left) Aviation Strobe
  const greenNavMat = new THREE.MeshStandardMaterial({
    color: 0x00e676,
    emissive: 0x00e676,
    emissiveIntensity: 2.4,
    roughness: 0.2,
  }); // Starboard (Right) Aviation Strobe
  const strobeWhiteMat = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    emissive: 0xffffff,
    emissiveIntensity: 2.8,
    roughness: 0.1,
  }); // Rear Tail Beacon
  const cyanAccentMat = new THREE.MeshStandardMaterial({
    color: 0x00d8f6,
    emissive: 0x00d8f6,
    emissiveIntensity: 1.2,
    roughness: 0.2,
  }); // BERAM Tech Precision Cyan Accent
  const bladeCarbonMat = new THREE.MeshStandardMaterial({
    color: 0x1b1e23,
    roughness: 0.35,
    metalness: 0.55,
    side: THREE.DoubleSide,
  }); // High-Performance Carbon Composite Prop Blade
  const bladeTipMat = new THREE.MeshStandardMaterial({
    color: 0x00d8f6,
    emissive: 0x008ba3,
    emissiveIntensity: 0.85,
    roughness: 0.3,
    side: THREE.DoubleSide,
  }); // High-Vis Aero Winglet Safety Tip

  // 1. Central Aerodynamic Carbon Fuselage
  const mainChassis = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.15, 0.075, 8), carbonMat);
  mainChassis.scale.set(1.15, 1.0, 1.4); // Aerodynamically elongated along Z (nose-tail)
  mainChassis.position.set(0, 0.055, 0);
  group.add(mainChassis);

  // 2. Streamlined Top Canopy Cover
  const topCanopy = new THREE.Mesh(new THREE.CylinderGeometry(0.105, 0.125, 0.038, 8), canopyMat);
  topCanopy.scale.set(1.1, 1.0, 1.35);
  topCanopy.position.set(0, 0.095, 0);
  group.add(topCanopy);

  // 3. Forward Nose Cone & Sensor Turret (+Z direction)
  const noseSlope = new THREE.Mesh(new THREE.BoxGeometry(0.15, 0.055, 0.12), canopyMat);
  noseSlope.position.set(0, 0.065, 0.13);
  group.add(noseSlope);

  // 4K Optical FPV Camera & LiDAR Pod (Nose)
  const gimbalHousing = new THREE.Mesh(new THREE.SphereGeometry(0.028, 16, 16), titaniumMat);
  gimbalHousing.position.set(0, 0.038, 0.19);
  group.add(gimbalHousing);

  const cameraLens = new THREE.Mesh(new THREE.CylinderGeometry(0.015, 0.015, 0.01, 16), glassSensorMat);
  cameraLens.rotation.x = Math.PI / 2;
  cameraLens.position.set(0, 0.038, 0.205);
  group.add(cameraLens);

  const lidarWindow = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.015, 0.012), glassSensorMat);
  lidarWindow.position.set(0, 0.072, 0.19);
  group.add(lidarWindow);

  // 4. Rear Aerodynamic Tail Taper & Strobe (-Z direction)
  const tailTaper = new THREE.Mesh(new THREE.BoxGeometry(0.13, 0.05, 0.10), carbonMat);
  tailTaper.position.set(0, 0.065, -0.13);
  group.add(tailTaper);

  const tailStrobe = new THREE.Mesh(new THREE.SphereGeometry(0.012, 10, 10), strobeWhiteMat);
  tailStrobe.position.set(0, 0.065, -0.185);
  group.add(tailStrobe);

  // 5. Circular BERAM Logo Medallion (Center Top Deck)
  const logoRadius = 0.078;
  // Titanium Beveled Bezel Ring
  const logoRim = new THREE.Mesh(new THREE.CylinderGeometry(logoRadius + 0.006, logoRadius + 0.010, 0.012, 32), titaniumMat);
  logoRim.position.set(0, 0.113, 0);
  group.add(logoRim);

  // Subtle Cyan Ambient Ring
  const cyanRing = new THREE.Mesh(new THREE.CylinderGeometry(logoRadius + 0.003, logoRadius + 0.003, 0.014, 32), cyanAccentMat);
  cyanRing.position.set(0, 0.113, 0);
  group.add(cyanRing);

  // Dark Backing Plate
  const backingPlate = new THREE.Mesh(new THREE.CircleGeometry(logoRadius, 32), carbonMat);
  backingPlate.rotation.x = -Math.PI / 2;
  backingPlate.position.set(0, 0.119, 0);
  group.add(backingPlate);

  // High-Resolution BERAM Logo Textured Face
  const logoTex = getBeramLogoTexture();
  const logoMat = new THREE.MeshStandardMaterial({
    map: logoTex,
    roughness: 0.25,
    metalness: 0.15,
    transparent: true,
  });
  const logoMesh = new THREE.Mesh(new THREE.CircleGeometry(logoRadius * 0.96, 32), logoMat);
  logoMesh.rotation.set(-Math.PI / 2, 0, Math.PI); // Top of logo points forward toward nose (+Z)
  logoMesh.position.set(0, 0.121, 0);
  group.add(logoMesh);

  // 6. Undercarriage Cargo Bay & Delivery Gripper Latch
  const bellyPlate = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.02, 0.22), carbonMat);
  bellyPlate.position.set(0, 0.015, 0);
  group.add(bellyPlate);

  const cargoLatch = new THREE.Mesh(new THREE.CylinderGeometry(0.028, 0.032, 0.016, 16), titaniumMat);
  cargoLatch.position.set(0, 0.0, 0);
  group.add(cargoLatch);

  const latchGlow = new THREE.Mesh(new THREE.SphereGeometry(0.009, 8, 8), cyanAccentMat);
  latchGlow.position.set(0, -0.008, 0);
  group.add(latchGlow);

  // 7. Dual Aerospace Carbon Landing Skids
  [-0.13, 0.13].forEach((x) => {
    // Longitudinal Ground Skid Tube (along Z)
    const skidGeo = new THREE.CylinderGeometry(0.007, 0.007, 0.32, 10);
    const skid = new THREE.Mesh(skidGeo, carbonMat);
    skid.rotation.x = Math.PI / 2;
    skid.position.set(x, -0.075, 0);
    group.add(skid);

    // Skid End Caps (Smooth curved tips)
    [0.16, -0.16].forEach((z) => {
      const cap = new THREE.Mesh(new THREE.SphereGeometry(0.009, 8, 8), titaniumMat);
      cap.position.set(x, -0.075, z);
      group.add(cap);
    });

    // Front & Rear Aerodynamic Angled Struts connecting to fuselage
    [0.08, -0.08].forEach((z) => {
      const strutGeo = new THREE.CylinderGeometry(0.006, 0.008, 0.11, 10);
      const strut = new THREE.Mesh(strutGeo, carbonMat);
      strut.position.set(x * 0.72, -0.028, z);
      strut.rotation.z = x > 0 ? -0.42 : 0.42;
      group.add(strut);
    });
  });

  // 8. Carbon Fiber Arms (Wide-X Configuration)
  const armLen = 0.30;
  const armAngles = arms === 6
    ? Array.from({ length: 6 }, (_, i) => (i * Math.PI) / 3)
    : [
        Math.PI * 0.25,  // Front-Left (45 deg)
        Math.PI * 0.75,  // Rear-Left (135 deg)
        Math.PI * 1.25,  // Rear-Right (225 deg)
        Math.PI * 1.75,  // Front-Right (315 deg)
      ];

  armAngles.forEach((ang, idx) => {
    const dx = Math.sin(ang);
    const dz = Math.cos(ang);

    // Tubular High-Modulus Carbon Fiber Arm
    const armGeo = new THREE.CylinderGeometry(0.013, 0.015, armLen * 0.92, 12);
    const arm = new THREE.Mesh(armGeo, carbonMat);
    arm.position.set((dx * armLen) / 2, 0.09, (dz * armLen) / 2);
    arm.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), new THREE.Vector3(dx, 0, dz).normalize());
    group.add(arm);

    // CNC Aluminum Motor Mount Bracket
    const motorMount = new THREE.Mesh(new THREE.CylinderGeometry(0.028, 0.034, 0.02, 16), titaniumMat);
    motorMount.position.set(dx * armLen, 0.095, dz * armLen);
    group.add(motorMount);

    // Brushless Motor Bell
    const motorBell = new THREE.Mesh(new THREE.CylinderGeometry(0.030, 0.030, 0.024, 20), titaniumMat);
    motorBell.position.set(dx * armLen, 0.115, dz * armLen);
    group.add(motorBell);

    // Motor Prop Spinner Cap
    const spinnerCap = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.016, 0.024, 12), carbonMat);
    spinnerCap.position.set(dx * armLen, 0.138, dz * armLen);
    group.add(spinnerCap);

    // Aviation Navigation Strobes at Motor Pods
    let navLightMat = null;
    if (dx < 0 && dz > 0) navLightMat = redNavMat;       // Front-Left = Red
    else if (dx > 0 && dz > 0) navLightMat = greenNavMat; // Front-Right = Green
    else navLightMat = cyanAccentMat;                    // Rear = Cyan

    const navLight = new THREE.Mesh(new THREE.SphereGeometry(0.009, 8, 8), navLightMat);
    navLight.position.set(dx * armLen, 0.08, dz * armLen);
    group.add(navLight);

    // 9. Aerodynamic High-Performance Propellers (Spins about Y axis)
    const propGroup = new THREE.Group();
    propGroup.name = 'propeller';
    propGroup.position.set(dx * armLen, 0.142, dz * armLen);

    const propRadius = 0.185;
    for (let b = 0; b < 2; b++) {
      const bladeAng = b * Math.PI;

      // Realistic tapered aerodynamic carbon blade
      const bladeGeo = new THREE.BoxGeometry(0.024, 0.004, propRadius * 0.88);
      const blade = new THREE.Mesh(bladeGeo, bladeCarbonMat);
      blade.position.set(0, 0, (propRadius * 0.88) / 2);
      blade.rotation.x = 0.11; // Aerodynamic angle of attack

      // High-visibility aero safety tip
      const tipGeo = new THREE.BoxGeometry(0.020, 0.003, propRadius * 0.14);
      const tip = new THREE.Mesh(tipGeo, bladeTipMat);
      tip.position.set(0, 0, propRadius * 0.94);
      tip.rotation.x = 0.11;

      const singleBlade = new THREE.Group();
      singleBlade.rotation.y = bladeAng;
      singleBlade.add(blade);
      singleBlade.add(tip);
      propGroup.add(singleBlade);
    }

    group.add(propGroup);
  });

  return group;
}

function buildHelicopterMesh() {
  const group = new THREE.Group();
  const shell = new THREE.MeshStandardMaterial({ color: 0x1a1d20, roughness: 0.45, metalness: 0.75 }); // dark graphite
  const carbon = new THREE.MeshStandardMaterial({ color: 0x0f1113, roughness: 0.6, metalness: 0.4 }); // matte dark gray/carbon
  const neonCyan = new THREE.MeshStandardMaterial({
    color: 0x00f3ff,
    emissive: 0x00f3ff,
    emissiveIntensity: 1.5,
    roughness: 0.1,
    metalness: 0.1
  });

  // Sleek fuselage
  const fuse = new THREE.Mesh(new THREE.SphereGeometry(0.22, 20, 16), shell);
  fuse.scale.set(0.7, 0.72, 1.5);
  group.add(fuse);

  // Tail boom
  const boom = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.02, 0.75, 10), shell);
  boom.rotation.x = Math.PI / 2;
  boom.position.set(0, 0.04, -0.5);
  group.add(boom);

  // Tail vertical fin
  const fin = new THREE.Mesh(new THREE.BoxGeometry(0.02, 0.17, 0.11), shell);
  fin.position.set(0, 0.12, -0.83);
  group.add(fin);

  // Rotor Mast
  const mast = new THREE.Mesh(new THREE.CylinderGeometry(0.018, 0.018, 0.14, 8), carbon);
  mast.position.set(0, 0.25, 0.03);
  group.add(mast);

  // Glowing accents on the helicopter body
  const leftGlow = new THREE.Mesh(new THREE.BoxGeometry(0.005, 0.02, 0.35), neonCyan);
  leftGlow.position.set(-0.155, 0.02, 0.1);
  group.add(leftGlow);

  const rightGlow = new THREE.Mesh(new THREE.BoxGeometry(0.005, 0.02, 0.35), neonCyan);
  rightGlow.position.set(0.155, 0.02, 0.1);
  group.add(rightGlow);

  const noseGlow = new THREE.Mesh(new THREE.SphereGeometry(0.03, 8, 8), neonCyan);
  noseGlow.position.set(0, -0.08, 0.28);
  group.add(noseGlow);

  // Main Rotor (4 blades, glowing cyan)
  const bladeMat = new THREE.MeshStandardMaterial({
    color: 0x00f3ff,
    emissive: 0x00f3ff,
    emissiveIntensity: 1.2,
    transparent: true,
    opacity: 0.8,
    roughness: 0.2,
    side: THREE.DoubleSide
  });

  const mainRotor = new THREE.Group();
  mainRotor.name = 'mainrotor';
  mainRotor.position.set(0, 0.33, 0.03);

  const mainBladeGeo = new THREE.BoxGeometry(1.5, 0.008, 0.06);
  // Add 4 blades separated by 90 degrees (Math.PI / 2)
  [0, Math.PI / 2, Math.PI, Math.PI * 1.5].forEach((r) => {
    const b = new THREE.Mesh(mainBladeGeo, bladeMat);
    b.rotation.y = r;
    mainRotor.add(b);
  });
  group.add(mainRotor);

  // Tail Rotor (glowing cyan)
  const tailRotor = new THREE.Group();
  tailRotor.name = 'tailrotor';
  tailRotor.position.set(0.06, 0.08, -0.85);

  const tailBladeGeo = new THREE.BoxGeometry(0.01, 0.3, 0.03);
  [0, Math.PI / 2].forEach((r) => {
    const b = new THREE.Mesh(tailBladeGeo, bladeMat);
    b.rotation.x = r;
    tailRotor.add(b);
  });
  group.add(tailRotor);

  // Sleek Carbon Landing Skids
  [-0.13, 0.13].forEach((x) => {
    const skid = new THREE.Mesh(new THREE.BoxGeometry(0.02, 0.02, 0.5), carbon);
    skid.position.set(x, -0.22, 0.02);
    group.add(skid);

    const leg = new THREE.Mesh(new THREE.BoxGeometry(0.015, 0.14, 0.015), carbon);
    leg.position.set(x, -0.15, 0.02);
    leg.rotation.z = x > 0 ? 0.12 : -0.12;
    group.add(leg);

    // Tiny neon tips on skids
    const skidGlow = new THREE.Mesh(new THREE.BoxGeometry(0.022, 0.022, 0.05), neonCyan);
    skidGlow.position.set(x, -0.22, 0.27);
    group.add(skidGlow);
  });

  return group;
}

function buildFixedWingMesh() {
  const group = new THREE.Group();
  const shell = new THREE.MeshStandardMaterial({ color: 0x1a1d20, roughness: 0.45, metalness: 0.75 }); // dark graphite
  const carbon = new THREE.MeshStandardMaterial({ color: 0x0f1113, roughness: 0.6, metalness: 0.4 }); // matte carbon
  const neonCyan = new THREE.MeshStandardMaterial({
    color: 0x00f3ff,
    emissive: 0x00f3ff,
    emissiveIntensity: 1.5,
    roughness: 0.1,
    metalness: 0.1
  });

  // Sleek fuselage
  const fuse = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.05, 1.0, 16), shell);
  fuse.rotation.x = Math.PI / 2;
  group.add(fuse);

  // Tapered nose cone
  const nose = new THREE.Mesh(new THREE.ConeGeometry(0.08, 0.22, 16), shell);
  nose.rotation.x = -Math.PI / 2;
  nose.position.z = 0.6;
  group.add(nose);

  // Carbon Wings
  const wing = new THREE.Mesh(new THREE.BoxGeometry(1.4, 0.025, 0.26), carbon);
  wing.position.set(0, 0.02, 0.02);
  group.add(wing);

  // Carbon Tail Stabilizers
  const tail = new THREE.Mesh(new THREE.BoxGeometry(0.52, 0.02, 0.14), carbon);
  tail.position.set(0, 0.01, -0.42);
  group.add(tail);

  const vstab = new THREE.Mesh(new THREE.BoxGeometry(0.02, 0.17, 0.14), carbon);
  vstab.position.set(0, 0.1, -0.44);
  group.add(vstab);

  // Neon-Cyan side stripes on fuselage
  const leftStripe = new THREE.Mesh(new THREE.BoxGeometry(0.005, 0.015, 0.55), neonCyan);
  leftStripe.position.set(-0.082, 0, 0.1);
  group.add(leftStripe);

  const rightStripe = new THREE.Mesh(new THREE.BoxGeometry(0.005, 0.015, 0.55), neonCyan);
  rightStripe.position.set(0.082, 0, 0.1);
  group.add(rightStripe);

  // Glowing Wingtip navigation bands
  const leftWingtip = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.03, 0.262), neonCyan);
  leftWingtip.position.set(-0.66, 0.02, 0.02);
  group.add(leftWingtip);

  const rightWingtip = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.03, 0.262), neonCyan);
  rightWingtip.position.set(0.66, 0.02, 0.02);
  group.add(rightWingtip);

  // Glowing Cyan Nose Propeller
  const propMat = new THREE.MeshStandardMaterial({
    color: 0x00f3ff,
    emissive: 0x00f3ff,
    emissiveIntensity: 1.2,
    transparent: true,
    opacity: 0.8,
    roughness: 0.2
  });

  const prop = new THREE.Mesh(new THREE.BoxGeometry(0.02, 0.44, 0.03), propMat);
  prop.position.set(0, 0, 0.72);
  group.add(prop);

  return group;
}

function buildProceduralDroneTemplate(modelUrl) {
  if (modelUrl === VEHICLE_MODEL_URL_MAP.helicopter) return buildHelicopterMesh();
  if (modelUrl === VEHICLE_MODEL_URL_MAP.fixedwing) return buildFixedWingMesh();
  if (modelUrl === VEHICLE_MODEL_URL_MAP.hexacopter) return buildMultirotorMesh(6);
  return buildMultirotorMesh(4);
}

// Drone bodies are procedural THREE.js now — no network load. Returns a resolved
// promise so the existing preload/modelReady/clone pipeline works unchanged.
function getCachedDroneModelTemplate(modelUrl) {
  if (modelTemplateCache.has(modelUrl)) {
    return Promise.resolve(modelTemplateCache.get(modelUrl));
  }
  if (!modelPromiseCache.has(modelUrl)) {
    const template = buildProceduralDroneTemplate(modelUrl);
    modelTemplateCache.set(modelUrl, template);
    modelPromiseCache.set(modelUrl, Promise.resolve(template));
  }
  return modelPromiseCache.get(modelUrl);
}

function offsetLatLon(lat, lon, bearingDeg, distanceMeters) {
  const rad = (bearingDeg * Math.PI) / 180;
  const metersPerDegLat = 111320;
  const metersPerDegLon = Math.max(1, 111320 * Math.cos((lat * Math.PI) / 180));
  const dLat = (Math.cos(rad) * distanceMeters) / metersPerDegLat;
  const dLon = (Math.sin(rad) * distanceMeters) / metersPerDegLon;
  return {
    lat: lat + dLat,
    lon: lon + dLon,
  };
}

function buildCirclePolygon(lat, lon, radiusMeters, steps) {
  const coords = [];
  const pointCount = Math.max(12, steps || 0);
  for (let i = 0; i <= pointCount; i += 1) {
    const bearing = (i / pointCount) * 360;
    const point = offsetLatLon(lat, lon, bearing, radiusMeters);
    coords.push([point.lon, point.lat]);
  }
  return {
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: [coords],
    },
    properties: {},
  };
}

function ensureTerrainAndBuildings(map, shouldShowBuildings) {
  if (!shouldShowBuildings) {
    if (map.getLayer('utms-3d-buildings')) {
      map.removeLayer('utms-3d-buildings');
    }
    if (map.getTerrain()) {
      map.setTerrain(null);
    }
    if (map.getSource('utms-dem')) {
      map.removeSource('utms-dem');
    }
    map.setFog(null);
    return;
  }

  if (!map.getSource('utms-dem')) {
    map.addSource('utms-dem', {
      type: 'raster-dem',
      url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
      tileSize: 512,
      maxzoom: 14,
    });
  }

  map.setTerrain({ source: 'utms-dem', exaggeration: 1.15 });
  map.setFog({
    range: [0.8, 8],
    color: 'rgba(23, 42, 76, 0.45)',
    'horizon-blend': 0.2,
    'space-color': 'rgba(5, 8, 22, 0.9)',
    'high-color': 'rgba(94, 188, 255, 0.25)',
    'star-intensity': 0.1,
  });

  if (!map.getLayer('utms-3d-buildings')) {
    // Guard: the 'composite' source is a built-in Mapbox style source that
    // bundles vector tiles (buildings, roads, etc.). In mapbox-gl 3.17, its
    // internal index structures (familiesBySource) may not be populated when
    // this function runs during early style.load or while DEM sourcedata
    // events trigger aggressive repaints. Adding a layer that references
    // 'composite' before it's indexed causes:
    //   TypeError: can't access property "composite", i.familiesBySource is undefined
    // Fix: check the source exists, wrap in try/catch, and defer via a
    // one-shot sourcedata listener if it isn't ready yet.
    if (!map.getSource('composite')) {
      // Composite source not ready yet — defer until it appears.
      const onSourceData = (e) => {
        if (map.getSource('composite') && !map.getLayer('utms-3d-buildings')) {
          map.off('sourcedata', onSourceData);
          ensureTerrainAndBuildings(map, true);
        }
      };
      map.on('sourcedata', onSourceData);
      // Clean up if the map is removed before composite loads.
      map.once('remove', () => map.off('sourcedata', onSourceData));
      return;
    }

    try {
      const style = map.getStyle();
      if (!style || !style.layers) return;
      const layers = style.layers;
      const labelLayerId = layers.find((layer) => layer.type === 'symbol' && layer.layout && layer.layout['text-field'])?.id;

      map.addLayer(
        {
          id: 'utms-3d-buildings',
          source: 'composite',
          'source-layer': 'building',
          filter: ['==', ['get', 'extrude'], 'true'],
          type: 'fill-extrusion',
          minzoom: 12,
          paint: {
            'fill-extrusion-color': '#9fb0c4',
            'fill-extrusion-height': ['interpolate', ['linear'], ['zoom'], 12, 0, 15.5, ['get', 'height']],
            'fill-extrusion-base': ['interpolate', ['linear'], ['zoom'], 12, 0, 15.5, ['get', 'min_height']],
            'fill-extrusion-opacity': 0.78,
          },
        },
        labelLayerId
      );
    } catch (err) {
      // Swallow the error — the buildings layer will be retried on the next
      // style.load or show3DBuildings toggle. This prevents the
      // familiesBySource crash from propagating into the render loop.
      if (process.env.NODE_ENV === 'development') {
        console.warn('[UTMS] 3D buildings layer deferred (composite source not indexed yet):', err.message);
      }
    }
  }

  ensureDroneLayerOnTop(map);
}

function createDroneModelLayer({ posesRef, modelUrlByDroneRef, vehicleModelByDroneRef, onModelLoaded, onModelError, activeRoutesRef, deliveryDropsRef, configRef, winchStateRef, groundParcelsRef }) {
  let camera = null;
  let scene = null;
  let renderer = null;
  let mapInstance = null;
  let disposed = false;
  const droneModels = new Map();
  const pendingLoads = new Map();
  const smoothedPoseByDrone = new Map();
  let lastRotorFrameMs = 0;  // timestamp of previous frame, for rotor spin delta-time
  // Winch animation state, OWNED BY THE COMPONENT, not by this closure.
  //   winchStateByDrone: droneId -> { missionId, phase, dropKey, startMs, doneKeys:Set }
  //   groundParcels:     `${droneId}:${dropKey}` -> { lat, lon, alt } already released
  //
  // These used to be plain `new Map()` locals here, which meant every rebuild of
  // this layer wiped them — and the layer is rebuilt on a style load, a WebGL
  // context restore, and on coming back from the 2D view. Toggling 2D and back
  // mid-delivery therefore lost the parcel and forgot which drops were already
  // done, so a delivered parcel could be "delivered" a second time.
  const winchStateByDrone = winchStateRef.current;
  const groundParcels = groundParcelsRef.current;
  let winchGroup = null;  // reused rope + parcel meshes, positioned per drop each frame
  let ropeMesh = null;
  let parcelGroup = null;

  // Equirectangular metres between two lat/lon — cheap and plenty accurate at drop range.
  const horizDistMeters = (lat1, lon1, lat2, lon2) => {
    const dLat = (lat2 - lat1) * 111320;
    const dLon = (lon2 - lon1) * 111320 * Math.cos((lat1 * Math.PI) / 180);
    return Math.hypot(dLat, dLon);
  };

  const disposeDroneModel = (droneId) => {
    const entry = droneModels.get(droneId);
    if (entry?.model && scene) {
      scene.remove(entry.model);
    }
    droneModels.delete(droneId);
    smoothedPoseByDrone.delete(droneId);
  };

  const applyModelVisuals = (model) => {
    model.traverse((node) => {
      if (node && node.isMesh) {
        node.frustumCulled = false;

        const cloneAndFixMaterial = (material) => {
          if (!material) return material;
          const cloned = material.clone();

          if (cloned.map) {
            cloned.map.colorSpace = THREE.SRGBColorSpace;
            cloned.map.needsUpdate = true;
          }

          if (cloned.emissiveMap) {
            cloned.emissiveMap.colorSpace = THREE.SRGBColorSpace;
            cloned.emissiveMap.needsUpdate = true;
          }

          cloned.needsUpdate = true;
          return cloned;
        };

        if (Array.isArray(node.material)) {
          node.material = node.material.map(cloneAndFixMaterial);
        } else {
          node.material = cloneAndFixMaterial(node.material);
        }
      }
    });
  };

  // Build a simple cone fallback to use when the GLB model fails to load.
  // The cone is orange and 1.5m tall — clearly visible but unambiguous about being a fallback.
  const buildFallbackCone = () => {
    const geometry = new THREE.ConeGeometry(0.6, 1.5, 8);
    const material = new THREE.MeshStandardMaterial({
      color: 0xff6600,
      emissive: 0xff3300,
      emissiveIntensity: 0.4,
      roughness: 0.5,
    });
    const cone = new THREE.Mesh(geometry, material);
    cone.frustumCulled = false;
    return cone;
  };

  return {
    id: DRONE_MODEL_LAYER_ID,
    type: 'custom',
    renderingMode: '3d',
    onAdd: (map, gl) => {
      mapInstance = map;
      camera = new THREE.Camera();
      scene = new THREE.Scene();

      const ambient = new THREE.AmbientLight(0xffffff, 0.92);
      const sun = new THREE.DirectionalLight(0xffffff, 0.72);
      sun.position.set(0, -65, 115);
      scene.add(ambient);
      scene.add(sun);

      renderer = new THREE.WebGLRenderer({
        canvas: map.getCanvas(),
        context: gl,
        antialias: true,
        powerPreference: 'high-performance',
      });
      renderer.autoClear = false;
      renderer.outputColorSpace = THREE.SRGBColorSpace;

      // Winch rope + parcel — one reusable group, repositioned per drop each frame.
      // Vertices/positions are kept as small local mercator deltas (the world
      // translation goes in the projection matrix) so float32 geometry stays precise.
      // Rope: a thin cylinder (not a 1px line) so it reads as a real winch cable and
      // catches light. Geometry axis is local Y; rotate it to the mercator up/down (Z)
      // axis once, then per-frame scale.y = rope length, scale.x/z = rope radius.
      const ropeGeo = new THREE.CylinderGeometry(1, 1, 1, 8);
      const ropeMat = new THREE.MeshStandardMaterial({ color: ROPE_COLOR, roughness: 0.9, metalness: 0.0 });
      ropeMesh = new THREE.Mesh(ropeGeo, ropeMat);
      ropeMesh.rotation.x = Math.PI / 2;   // local Y (cylinder length) -> world Z
      ropeMesh.frustumCulled = false;

      // Parcel: a kraft cardboard box with dark seam edges, a packing-tape strip and a
      // small metal hook where the rope attaches — a real-delivery look. All children are
      // unit-sized (1m cube); the group is scaled to PARCEL_SIZE_METERS per drop.
      // Real Enterprise Air Cargo Parcel Box (100% Solid & Opaque, BERAM Brand on All 4 Sides)
      parcelGroup = new THREE.Group();

      const boxGeo = createParcelBoxGeometry();
      const sideMat = new THREE.MeshStandardMaterial({
        map: getParcelSideTexture(),
        roughness: 0.7,
        metalness: 0.1,
        side: THREE.DoubleSide,
        transparent: false,
        opacity: 1.0,
      });
      const topBottomMat = new THREE.MeshStandardMaterial({
        color: 0xb58249, // solid kraft cardboard
        roughness: 0.8,
        metalness: 0.05,
        side: THREE.DoubleSide,
        transparent: false,
        opacity: 1.0,
      });

      const box = new THREE.Mesh(boxGeo, [sideMat, topBottomMat]);
      box.frustumCulled = false;
      parcelGroup.add(box);

      // Packing-tape strip across the top (+Z) face seam
      const tape = new THREE.Mesh(
        new THREE.BoxGeometry(0.24, 1.01, 0.02),
        new THREE.MeshStandardMaterial({
          color: PARCEL_TAPE_COLOR,
          roughness: 0.6,
          side: THREE.DoubleSide,
          transparent: false,
          opacity: 1.0,
        }),
      );
      tape.position.set(0, 0, 0.508);
      tape.frustumCulled = false;
      parcelGroup.add(tape);

      // Top Deck BERAM Inspection Stamp
      const topSeal = new THREE.Mesh(
        new THREE.CircleGeometry(0.16, 32),
        new THREE.MeshStandardMaterial({
          map: getBeramLogoTexture(),
          side: THREE.DoubleSide,
          transparent: true,
          roughness: 0.3,
        }),
      );
      topSeal.position.set(0, 0, 0.512);
      topSeal.frustumCulled = false;
      parcelGroup.add(topSeal);

      // Metal hook ring at the top where the rope threads in
      const hook = new THREE.Mesh(
        new THREE.TorusGeometry(0.08, 0.022, 8, 16),
        new THREE.MeshStandardMaterial({
          color: 0x555555,
          roughness: 0.4,
          metalness: 0.7,
        }),
      );
      hook.position.set(0, 0, 0.54);
      hook.frustumCulled = false;
      parcelGroup.add(hook);

      winchGroup = new THREE.Group();
      winchGroup.add(ropeMesh);
      winchGroup.add(parcelGroup);
      winchGroup.visible = false;
      scene.add(winchGroup);
    },
    render: (gl, matrix) => {
      if (disposed || !renderer || !camera || !scene || !mapInstance) {
        return;
      }
      const poses = posesRef.current || {};
      const modelUrlByDrone = modelUrlByDroneRef.current || {};
      const trackedIds = new Set(Object.keys(modelUrlByDrone));
      if (trackedIds.size === 0) {
        return;
      }

      const ensureDroneModel = (droneId, modelUrl) => {
        const existing = droneModels.get(droneId);
        if (existing && existing.url === modelUrl) {
          return;
        }

        if (existing) {
          disposeDroneModel(droneId);
        }

        const pending = pendingLoads.get(droneId);
        if (pending && pending.url === modelUrl) {
          return;
        }

        const loadPromise = getCachedDroneModelTemplate(modelUrl)
          .then((template) => {
            if (disposed) return;
            const latest = pendingLoads.get(droneId);
            if (!latest || latest.url !== modelUrl) return;
            const model = template.clone(true);
            applyModelVisuals(model);
            model.matrixAutoUpdate = false;
            model.matrixWorldAutoUpdate = false;
            const rotors = findRotorSpinners(model);
            scene.add(model);
            droneModels.set(droneId, { model, url: modelUrl, isFallback: false, rotors: rotors.length ? rotors : null });
            pendingLoads.delete(droneId);
            if (onModelLoaded) onModelLoaded(modelUrl);
            if (mapInstance) mapInstance.triggerRepaint();
          })
          .catch((error) => {
            if (disposed) return;
            const latest = pendingLoads.get(droneId);
            if (latest && latest.url === modelUrl) {
              pendingLoads.delete(droneId);
            }
            // Use orange cone fallback so the drone position is still visible
            if (!droneModels.has(droneId) && scene) {
              const fallback = buildFallbackCone();
              fallback.matrixAutoUpdate = false;
              fallback.matrixWorldAutoUpdate = false;
              scene.add(fallback);
              droneModels.set(droneId, { model: fallback, url: modelUrl, isFallback: true });
              if (mapInstance) mapInstance.triggerRepaint();
            }
            if (onModelError) onModelError(modelUrl, error);
          });

        pendingLoads.set(droneId, { url: modelUrl, promise: loadPromise });
      };

      trackedIds.forEach((droneId) => {
        const modelUrl = modelUrlByDrone[droneId] || DRONE_MODEL_URL;
        ensureDroneModel(droneId, modelUrl);
      });

      Array.from(droneModels.keys()).forEach((droneId) => {
        if (!trackedIds.has(droneId)) {
          disposeDroneModel(droneId);
          pendingLoads.delete(droneId);
        }
      });

      const mapMatrix = new THREE.Matrix4().fromArray(matrix);
      const renderEntries = [];
      const winchJobs = [];  // { lon, lat, droneAltMsl, extension, parcelAttached, metersToMU }
      const deliveryDrops = deliveryDropsRef?.current || {};

      const lerp = (a, b, t) => a + (b - a) * t;
      const lerpAngleDeg = (from, to, t) => {
        const diff = ((to - from + 540) % 360) - 180;
        return (((from + diff * t) % 360) + 360) % 360;
      };
      const clampTilt = (v) => Math.max(-DRONE_MAX_TILT_RAD, Math.min(DRONE_MAX_TILT_RAD, Number.isFinite(v) ? v : 0));
      let needsSettleRepaint = false;

      // Delta-time for rotor spin (capped so a stalled/backgrounded tab can't jump the blades).
      const nowMs = typeof performance !== 'undefined' ? performance.now() : Date.now();
      const rotorDt = lastRotorFrameMs ? Math.min((nowMs - lastRotorFrameMs) / 1000, 0.1) : 0;
      lastRotorFrameMs = nowMs;
      let hasAnimatedModel = false;

      Array.from(droneModels.entries()).forEach(([droneId, entry]) => {
        entry.model.visible = false;

        const pose = poses[droneId];
        if (!pose || !hasValidCoordinates(pose.lat, pose.lon)) {
          return;
        }

        const targetAlt = resolveModelAltitudeMeters(mapInstance, pose, droneId, activeRoutesRef) + DRONE_ALTITUDE_OFFSET_METERS;
        const targetMerc = mapboxgl.MercatorCoordinate.fromLngLat([pose.lon, pose.lat], targetAlt);
        const metersToMU = targetMerc.meterInMercatorCoordinateUnits();
        const targetPitch = clampTilt(pose.pitch);
        const targetRoll = clampTilt(pose.roll);

        // Smooth the rendered pose toward the latest telemetry fix so the model
        // glides instead of teleporting between ~2 Hz updates.
        let s = smoothedPoseByDrone.get(droneId);
        const jumpMeters = s ? Math.hypot(targetMerc.x - s.x, targetMerc.y - s.y) / metersToMU : Infinity;
        if (!s || jumpMeters > DRONE_SNAP_DISTANCE_METERS) {
          s = {
            x: targetMerc.x, y: targetMerc.y, alt: targetAlt,
            heading: Number.isFinite(pose.heading) ? pose.heading : 0,
            pitch: targetPitch, roll: targetRoll,
          };
        } else {
          s.x = lerp(s.x, targetMerc.x, DRONE_POS_LERP);
          s.y = lerp(s.y, targetMerc.y, DRONE_POS_LERP);
          s.alt = lerp(s.alt, targetAlt, 0.15);
          s.heading = lerpAngleDeg(s.heading, Number.isFinite(pose.heading) ? pose.heading : s.heading, DRONE_HEADING_LERP);
          s.pitch = lerp(s.pitch, targetPitch, DRONE_ATTITUDE_LERP);
          s.roll = lerp(s.roll, targetRoll, DRONE_ATTITUDE_LERP);
        }
        // 1. Forward pitch lean (Thrust vector angle proportional to clean EKF ground velocity)
        const telemetrySpeed = (pose && Number.isFinite(pose.speed)) ? pose.speed : 0;
        const targetLean = Math.min(FWD_LEAN_MAX_RAD, telemetrySpeed * FWD_LEAN_PER_MPS);
        s.leanPitch = lerp(s.leanPitch || 0, targetLean, 0.06);

        // 2. Coordinated turn banking (Centripetal roll with noise deadzone to eliminate jitter)
        const prevHeading = s.prevHeading !== undefined ? s.prevHeading : s.heading;
        s.prevHeading = s.heading;
        const yawDiffDeg = ((s.heading - prevHeading + 540) % 360) - 180;
        const smoothYaw = Math.abs(yawDiffDeg) > 0.06 ? yawDiffDeg : 0;
        const targetBank = Math.max(-0.35, Math.min(0.35, (smoothYaw * (Math.PI / 180) * Math.min(telemetrySpeed, 15)) * 0.08));
        s.bankRoll = lerp(s.bankRoll || 0, targetBank, 0.06);

        smoothedPoseByDrone.set(droneId, s);

        // Keep repainting until the smoothed pose settles on the target.
        if (
          Math.hypot(targetMerc.x - s.x, targetMerc.y - s.y) / metersToMU > 0.05 ||
          Math.abs(targetAlt - s.alt) > 0.05 ||
          Math.abs(((pose.heading - s.heading + 540) % 360) - 180) > 0.2
        ) {
          needsSettleRepaint = true;
        }

        const smoothedMercZ = mapboxgl.MercatorCoordinate.fromLngLat(
          [pose.lon, pose.lat], s.alt
        ).z;
        const vehicleModel = vehicleModelByDroneRef?.current?.[droneId] || '';
        const modelConfig = resolveVehicleModelConfig(vehicleModel);
        let mercatorScale = metersToMU * DRONE_MODEL_SCALE_METERS * modelConfig.scaleMultiplier;
        // Distance-compensated floor so the drone stays readable when zoomed out.
        let apparentBoost = 1;
        const camPos = mapInstance?.getFreeCameraOptions?.().position;
        if (camPos) {
          const distMU = Math.hypot(s.x - camPos.x, s.y - camPos.y, smoothedMercZ - (camPos.z || 0));
          const minScale = distMU * DRONE_MIN_APPARENT_SCALE;
          if (minScale > mercatorScale) {
            apparentBoost = minScale / mercatorScale;
            mercatorScale = minScale;
          }
        }
        // 180 - heading, not 360 - heading.
        const headingRad = ((180 - (s.heading + DRONE_HEADING_OFFSET_DEG + modelConfig.headingOffsetDeg)) * Math.PI) / 180;

        const rotationUpright = new THREE.Matrix4().makeRotationAxis(new THREE.Vector3(1, 0, 0), Math.PI / 2);
        const rotationYaw = new THREE.Matrix4().makeRotationAxis(new THREE.Vector3(0, 1, 0), headingRad);

        // Dynamic Physics: Combine MAVLink telemetry attitude with forward velocity lean and turning bank
        const travelLean = modelConfig.leansIntoTravel ? modelConfig.leanDir * (s.leanPitch || 0) : 0;
        const turnBank = modelConfig.banksIntoTurns ? (s.bankRoll || 0) : 0;

        const rotationPitch = new THREE.Matrix4().makeRotationAxis(new THREE.Vector3(1, 0, 0), travelLean - s.pitch);
        const rotationRoll = new THREE.Matrix4().makeRotationAxis(new THREE.Vector3(0, 0, 1), -s.roll + turnBank);

        const modelMatrix = new THREE.Matrix4()
          .makeTranslation(s.x, s.y, smoothedMercZ)
          .scale(new THREE.Vector3(mercatorScale, -mercatorScale, mercatorScale))
          .multiply(rotationUpright)
          .multiply(rotationYaw)
          .multiply(rotationPitch)
          .multiply(rotationRoll);

        renderEntries.push({ entry, modelMatrix, armed: !!pose.armed });

        // ── Winch delivery ── The parcel rides UNDER the drone from takeoff
        // ('carry'), pays out to the ground at the drop ('out'→'hold'), releases,
        // and the empty rope retracts ('in'); repeat for each remaining drop.
        const dd = deliveryDrops[droneId];
        if (dd && Array.isArray(dd.drops) && dd.drops.length) {
          let st = winchStateByDrone.get(droneId);
          // A NEW mission is the only thing that resets delivery progress. Layer
          // rebuilds must not, or the drone re-delivers parcels it already
          // dropped on this flight.
          if (!st || st.missionId !== dd.missionId) {
            st = { missionId: dd.missionId, phase: 'carry', dropKey: null, startMs: 0, doneKeys: new Set() };
          }
          const remaining = dd.drops.filter((d) => !st.doneKeys.has(d.key));

          // Carrying: arm ONLY once the drone has arrived AND settled at drop
          // height — within a tight band and no longer descending. The old wide
          // 6 m band armed mid-descent, so the parcel started dropping while the
          // drone was still coming down. Thresholds are config-driven.
          if (st.phase === 'carry') {
            const dtCfg = (configRef && configRef.current && configRef.current.dropTiming) || {};
            const proximity = Number.isFinite(dtCfg.winchArmProximityMeters) ? dtCfg.winchArmProximityMeters : 14;
            const settleBand = Number.isFinite(dtCfg.settleBandMeters) ? dtCfg.settleBandMeters : 5.0;
            const settleRate = Number.isFinite(dtCfg.settleDescentRateMps) ? dtCfg.settleDescentRateMps : 0.6;

            // Vertical speed from AGL changes (telemetry ~2 Hz). Update only when
            // the value actually changes so a fast descent reads as fast, and a
            // steady hover reads as "stable" (no recent change) → settled.
            if (Number.isFinite(pose.altitudeAgl)) {
              if (st.lastAgl === undefined) { st.lastAgl = pose.altitudeAgl; st.lastAglMs = nowMs; st.vSpeed = 999; }
              else if (pose.altitudeAgl !== st.lastAgl) {
                const dtSec = (nowMs - (st.lastAglMs || nowMs)) / 1000;
                if (dtSec > 0.02) st.vSpeed = (pose.altitudeAgl - st.lastAgl) / dtSec;
                st.lastAgl = pose.altitudeAgl; st.lastAglMs = nowMs;
              }
            }
            const stableHover = (nowMs - (st.lastAglMs || 0)) > 700; // AGL steady → hovering
            const settled = stableHover || Math.abs(st.vSpeed || 0) <= settleRate;

            if (remaining.length === 0) {
              st.phase = 'idle';  // all delivered — fly the empty RTL, no parcel
            } else if (Number.isFinite(pose.altitudeAgl)) {
              for (const drop of remaining) {
                const near = horizDistMeters(pose.lat, pose.lon, drop.lat, drop.lon) <= proximity;
                const atHeight = Math.abs(pose.altitudeAgl - dd.dropHeight) <= settleBand;
                if (near && (atHeight || settled)) { st.phase = 'out'; st.dropKey = drop.key; st.startMs = nowMs; break; }
              }
            }
          }

          if (st.phase !== 'idle') {
            const drop = dd.drops.find((d) => d.key === st.dropKey);
            const elapsed = nowMs - st.startMs;
            const winchMs = dd.winchMs || { out: WINCH_OUT_MS, hold: WINCH_HOLD_MS, in: WINCH_IN_MS };
            let extension = WINCH_CARRY_LENGTH_METERS;
            let parcelAttached = true;
            if (st.phase === 'carry') {
              // Hang just below the drone; on the pad shorten so the box rests on
              // the ground instead of sinking through it as the drone climbs.
              extension = Number.isFinite(pose.altitudeAgl)
                ? Math.min(WINCH_CARRY_LENGTH_METERS, Math.max(0.05, pose.altitudeAgl - PARCEL_SIZE_METERS))
                : WINCH_CARRY_LENGTH_METERS;
            } else if (st.phase === 'out') {
              extension = lerp(WINCH_CARRY_LENGTH_METERS, dd.ropeLength, Math.min(1, elapsed / winchMs.out));
              if (elapsed >= winchMs.out) { st.phase = 'hold'; st.startMs = nowMs; }
            } else if (st.phase === 'hold') {
              extension = dd.ropeLength;
              if (elapsed >= winchMs.hold) {
                // Release: the parcel stays on the ground at the rope's full depth.
                if (drop) groundParcels.set(`${droneId}:${st.dropKey}`, { lat: drop.lat, lon: drop.lon, alt: targetAlt - dd.ropeLength });
                st.phase = 'in'; st.startMs = nowMs;
              }
            } else if (st.phase === 'in') {
              parcelAttached = false;  // empty rope retracting
              extension = lerp(dd.ropeLength, 0, Math.min(1, elapsed / winchMs.in));
              if (elapsed >= winchMs.in) {
                st.doneKeys.add(st.dropKey); st.dropKey = null;
                // Next parcel if drops remain, else fly home empty.
                st.phase = dd.drops.some((d) => !st.doneKeys.has(d.key)) ? 'carry' : 'idle';
                st.startMs = nowMs;
              }
            }
            if (st.phase !== 'idle') {
              // Anchor rope/parcel to the SMOOTHED drone position (same s.x/s.y/s.alt the
              // model renders at) — NOT the raw ~2Hz telemetry fix. Otherwise the model
              // glides while the rope/parcel snap to each fix, so they visibly detach and
              // flicker apart in flight. Same apparent-size boost keeps them visible zoomed out.
              const smLngLat = new mapboxgl.MercatorCoordinate(s.x, s.y, smoothedMercZ).toLngLat();
              winchJobs.push({ lon: smLngLat.lng, lat: smLngLat.lat, droneAltMsl: s.alt, extension, parcelAttached, metersToMU: metersToMU * apparentBoost });
              needsSettleRepaint = true;  // keep repainting through the animation
            }
          }
          winchStateByDrone.set(droneId, st);
        }
      });

      // No sweep here on purpose. Entries are keyed by mission and reset when the
      // mission changes, so a stale one is inert; deleting on "no drops this
      // frame" is exactly what made a mid-flight refetch forget the delivery.
      // The maps die with the component.

      if (needsSettleRepaint && mapInstance) {
        mapInstance.triggerRepaint();
      }

      if (renderEntries.length === 0) {
        return;
      }

      renderer.resetState();
      // Always clear depth so the drone model renders on top of the map terrain and buildings
      // Mapbox v3 depth buffer can occlude Three.js models if not cleared
      renderer.clearDepth();

      renderEntries.forEach(({ entry, modelMatrix, armed }) => {
        entry.model.visible = true;
        // Spin rotors, but only while the FC reports armed — the blades stop where
        // they are otherwise. This gates the built multirotor mesh too: its prop
        // groups are named 'propeller', so findRotorSpinners picks them up and they
        // used to turn on a disarmed aircraft.
        // The model root has matrixWorldAutoUpdate off, so we advance the rotor's
        // local rotation and force a world-matrix recompute here.
        if (entry.rotors && armed) {
          for (const rotor of entry.rotors) {
            rotor.obj.rotation[rotor.axis] = (rotor.obj.rotation[rotor.axis] + rotor.speed * rotorDt) % (Math.PI * 2);
          }
          entry.model.updateMatrixWorld(true);
          hasAnimatedModel = true;
        }
        const projectionMatrix = new THREE.Matrix4().copy(mapMatrix).multiply(modelMatrix);
        camera.projectionMatrix.copy(projectionMatrix);
        if (camera.projectionMatrixInverse) {
          camera.projectionMatrixInverse.copy(camera.projectionMatrix).invert();
        }
        renderer.render(scene, camera);
        entry.model.visible = false;
      });

      // ── Render winch ropes (hanging) and released parcels (on the ground) ──
      // All drone models are hidden now; the winch group renders alone against a
      // pure world translation (no drone scale/rotation) so the rope hangs straight.
      if (winchGroup && (winchJobs.length || groundParcels.size)) {
        winchGroup.visible = true;

        const renderWinchAt = (lon, lat, topAltMsl, dzMeters, metersToMU, showRope, showParcel, onGround) => {
          const topMerc = mapboxgl.MercatorCoordinate.fromLngLat([lon, lat], topAltMsl);
          const botMerc = mapboxgl.MercatorCoordinate.fromLngLat([lon, lat], topAltMsl - dzMeters);
          const dz = botMerc.z - topMerc.z;  // exact mercator depth delta (negative = down)
          ropeMesh.visible = showRope;
          if (showRope) {
            const r = ROPE_RADIUS_METERS * metersToMU;
            ropeMesh.scale.set(r, Math.abs(dz), r);  // local Y (length) rotated to world Z
            ropeMesh.position.set(0, 0, dz / 2);      // span 0 (drone) .. dz (rope tip)
          }
          parcelGroup.visible = showParcel;
          if (showParcel) {
            const sz = PARCEL_SIZE_METERS * metersToMU;
            const half = sz / 2;
            parcelGroup.scale.set(sz, sz, sz);
            // Hanging: box top face meets the rope tip (dz). Ground: box rests on the
            // surface (bottom at z=0). +Z is up, so subtract half to drop below the tip.
            parcelGroup.position.set(0, 0, onGround ? half : dz - half);
          }
          winchGroup.updateMatrixWorld(true);
          const modelMatrix = new THREE.Matrix4().makeTranslation(topMerc.x, topMerc.y, topMerc.z);
          const projectionMatrix = new THREE.Matrix4().copy(mapMatrix).multiply(modelMatrix);
          camera.projectionMatrix.copy(projectionMatrix);
          if (camera.projectionMatrixInverse) {
            camera.projectionMatrixInverse.copy(camera.projectionMatrix).invert();
          }
          renderer.render(scene, camera);
        };

        // Hanging rope + parcel below each actively-dropping drone.
        winchJobs.forEach((job) => {
          renderWinchAt(job.lon, job.lat, job.droneAltMsl, job.extension, job.metersToMU, true, job.parcelAttached, false);
        });

        // Parcels already released, sitting on the ground.
        groundParcels.forEach((gp) => {
          const m2mu = mapboxgl.MercatorCoordinate.fromLngLat([gp.lon, gp.lat], gp.alt).meterInMercatorCoordinateUnits();
          renderWinchAt(gp.lon, gp.lat, gp.alt, 0, m2mu, false, true, true);
        });

        winchGroup.visible = false;
        mapInstance.triggerRepaint();
      }

      // Keep repainting while a rotor is spinning so the blades animate continuously.
      if (hasAnimatedModel && mapInstance) {
        mapInstance.triggerRepaint();
      }
    },
    onRemove: () => {
      disposed = true;
      Array.from(droneModels.keys()).forEach((droneId) => disposeDroneModel(droneId));
      pendingLoads.clear();
      if (scene) {
        scene.clear();
      }
      if (renderer) {
        renderer.dispose();
      }
      camera = null;
      scene = null;
      renderer = null;
      mapInstance = null;
    },
  };
}

// Custom Mapbox layer that draws mission routes as true-altitude 3D geometry:
// a floating rectangular box-tunnel the drone flies through, a bright centre
// line, a sphere at each waypoint, and a vertical "drop line" to the ground so
// altitude reads clearly. Reads route data from routeDataRef and only rebuilds
// geometry when its `version` changes (cheap per-frame rendering).
function createRouteRibbonLayer({ routeDataRef, activeRoutesRef, configRef, trailsRef }) {
  let camera = null;
  let scene = null;
  let renderer = null;
  let mapInstance = null;
  let disposed = false;
  let builtVersion = -1;
  let originMerc = null;       // shared origin in mercator units
  let originScale = 1;         // metres -> mercator units at the origin
  let resolvedRoutesList = []; // list of resolved routes to check terrain updates
  let lastTerrainScanMs = 0;   // throttle for the per-frame terrain drift self-heal
  const groundElevationCache = new Map();

  const getCacheKey = (lat, lon) => {
    return `${lat.toFixed(5)},${lon.toFixed(5)}`;
  };
  class CustomPathCurve extends THREE.Curve {
    constructor(points) {
      super();
      this.points = points;
    }
    getPoint(t, optionalTarget = new THREE.Vector3()) {
      const pointIndex = t * (this.points.length - 1);
      const index = Math.floor(pointIndex);
      const weight = pointIndex - index;

      if (index >= this.points.length - 1) {
        return optionalTarget.copy(this.points[this.points.length - 1]);
      }

      const p1 = this.points[index];
      const p2 = this.points[index + 1];
      return optionalTarget.lerpVectors(p1, p2, weight);
    }
  }

  const group = new THREE.Group();
  const disposables = [];
  // Where each drone has actually flown this mission (trailsRef, filled from
  // telemetry by the component): a thin white line inside the planned tube.
  const trailGroup = new THREE.Group();
  const trailDisposables = [];
  let builtTrailVersion = -1;

  const clearGroup = () => {
    group.clear();
    disposables.forEach((d) => { try { d.dispose(); } catch { /* already gone */ } });
    disposables.length = 0;
  };

  // Live terrain elevation (MSL metres) under a point, null if unavailable.
  const queryGround = (lat, lon) => {
    const key = getCacheKey(lat, lon);
    if (mapInstance?.queryTerrainElevation) {
      const t = mapInstance.queryTerrainElevation([lon, lat], TERRAIN_QUERY_OPTIONS);
      if (Number.isFinite(t)) {
        groundElevationCache.set(key, t);
        return t;
      }
    }
    if (groundElevationCache.has(key)) {
      return groundElevationCache.get(key);
    }
    return null;
  };

  // The corridor follows the mission EXACTLY: it is drawn as straight segments
  // between consecutive points (home → WP1 → WP2 → … → land) at each point's
  // real resolved altitude. So altitude changes appear as DIAGONAL ramps — the
  // drone climbs to WP1's height, then ramps to WP2's height, etc., matching how
  // it actually flies. No artificial vertical-step / level shaping. (The level
  // approach + vertical descent at LAND is added where routes are built, below.)
  // Drapes cruise legs over terrain: if a segment travels horizontally across
  // terrain, sample intermediate points every ~15m so the 3D corridor tube
  // bends and curves smoothly over hills and valleys instead of slicing straight through them.
  // Vertical legs (takeoff climb, landing/drop vertical descents) remain straight columns.
  const shapeFlightProfile = (pts) => {
    if (!Array.isArray(pts) || pts.length < 2) return pts;
    const out = [];
    const STEP_METERS = 15;

    for (let i = 0; i < pts.length; i++) {
      const curr = pts[i];
      out.push(curr);
      if (i === pts.length - 1) break;

      const next = pts[i + 1];
      const dLatM = (next.lat - curr.lat) * 111320;
      const dLonM = (next.lon - curr.lon) * 111320 * Math.cos(((curr.lat + next.lat) * 0.5 * Math.PI) / 180);
      const horizDist = Math.hypot(dLatM, dLonM);

      // Only drape horizontal legs (skip vertical climbs/descents where horizDist <= 1.5m)
      if (horizDist > STEP_METERS) {
        const steps = Math.min(100, Math.floor(horizDist / STEP_METERS));
        if (steps >= 2) {
          for (let s = 1; s < steps; s++) {
            const t = s / steps;
            const lat = curr.lat + t * (next.lat - curr.lat);
            const lon = curr.lon + t * (next.lon - curr.lon);
            const baseAgl = (curr.agl !== undefined && next.agl !== undefined)
              ? curr.agl + t * (next.agl - curr.agl)
              : (curr.agl ?? 30);

            let ground = queryGround(lat, lon);
            if (ground === null || !Number.isFinite(ground)) {
              ground = curr.groundAlt + t * (next.groundAlt - curr.groundAlt);
            }

            const displayAgl = Math.max(Number.isFinite(baseAgl) ? baseAgl : 0, ROUTE_MIN_DISPLAY_AGL_METERS);
            out.push({
              lat,
              lon,
              alt: ground + displayAgl,
              groundAlt: ground,
              agl: baseAgl,
              type: curr.type,
              synthetic: true, // Draper point: no waypoint sphere
            });
          }
        }
      }
    }
    return out;
  };

  // Absolute {lat, lon, alt(MSL m)} -> THREE.Vector3 in METRES relative to the
  // shared origin. Building geometry at human scale (metres) keeps Three.js
  // tube/extrude maths well-conditioned; the render matrix scales metres back
  // into mercator units.
  const toLocal = (lat, lon, altMeters) => {
    const m = mapboxgl.MercatorCoordinate.fromLngLat([lon, lat], altMeters);
    return new THREE.Vector3(
      (m.x - originMerc.x) / originScale,
      (m.y - originMerc.y) / originScale,
      (m.z - originMerc.z) / originScale
    );
  };

  const rebuildGeometry = () => {
    clearGroup();
    originMerc = null;

    const data = routeDataRef.current || { routes: [] };
    const routes = data.routes || [];

    // Resolve every route's raw waypoints using two passes:
    // Pass 1 queries current/cached terrain elevation.
    // Pass 2 propagates the nearest known terrain elevation as a fallback to prevent plunging to 0m.
    const resolvedRoutes = (routes || [])
      .filter(Boolean)
      .map((route) => {
        const rawPts = (route?.points || [])
          .filter((p) => p && hasValidCoordinates(p.lat, p.lon));
        if (rawPts.length === 0) return null;

        const ptsWithTerrain = rawPts.map((p) => {
          const ground = queryGround(p.lat, p.lon);
          return {
            lat: p.lat,
            lon: p.lon,
            // Route waypoint altitudes are authored in METRES by the planner, so
            // read them directly. Do NOT apply normalizeAltitudeMeters here — its
            // mm-guess (>2000 → /1000) would corrupt a legitimate high absolute-MSL
            // waypoint. That heuristic is only for raw telemetry (MAVLink mm).
            altN: Number.isFinite(Number(p.alt)) ? Number(p.alt) : 0,
            frame: Number(p.frame),
            type: String(p.type || '').toUpperCase(),
            synthetic: !!p.synthetic,
            rawGround: ground,
          };
        });

        let lastKnownGround = null;
        for (let i = 0; i < ptsWithTerrain.length; i++) {
          if (ptsWithTerrain[i].rawGround !== null) {
            lastKnownGround = ptsWithTerrain[i].rawGround;
            break;
          }
        }

        // If absolutely no points on this route have terrain loaded, try the map center or default to 0
        if (lastKnownGround === null && mapInstance) {
          const center = mapInstance.getCenter();
          const centerGround = queryGround(center.lat, center.lng);
          if (centerGround !== null) {
            lastKnownGround = centerGround;
          }
        }
        if (lastKnownGround === null) {
          lastKnownGround = 0;
        }

        const pts = ptsWithTerrain.map((p) => {
          let groundAlt = p.rawGround;
          if (groundAlt === null) {
            groundAlt = lastKnownGround;
          } else {
            lastKnownGround = groundAlt;
          }

          const isAbsolute = p.frame === MAV_FRAME_GLOBAL_ABSOLUTE_ALT;
          const requestedAgl = isAbsolute ? p.altN - groundAlt : p.altN;
          const displayAgl = Math.max(Number.isFinite(requestedAgl) ? requestedAgl : 0, ROUTE_MIN_DISPLAY_AGL_METERS);
          return {
            lat: p.lat,
            lon: p.lon,
            alt: groundAlt + displayAgl,
            groundAlt,
            agl: requestedAgl, // height above home/ground — picks the altitude-layer colour
            type: p.type,
            synthetic: !!p.synthetic,
          };
        });

        return {
          droneId: route?.droneId,
          active: !!route?.active,
          isTransition: !!route?.isTransition,
          color: route?.color || null,
          pts: shapeFlightProfile(pts),
        };
      })
      .filter((r) => r && r.pts.length >= 1);
    // A corridor's presence is driven by mission STATE (an `active` route is one
    // whose mission is actually running) — not a client-side countdown timer. So a
    // slow drone's corridor stays drawn until the flight ends, and a completed one
    // drops to a dim preview immediately. No timer-based expiry here.

    // Store resolved routes for terrain update checks in the render loop
    resolvedRoutesList = resolvedRoutes;
    
    // Cache the resolved active route points for drone model altitude projection
    const nextActiveRoutes = {};
    (resolvedRoutes || []).filter(Boolean).forEach((r) => {
      if (r && r.active && r.droneId) {
        nextActiveRoutes[r.droneId] = r.pts;
      }
    });
    if (activeRoutesRef) {
      activeRoutesRef.current = nextActiveRoutes;
    }

    if (resolvedRoutes.length === 0) return;

    const corridorCfg = (configRef && configRef.current && configRef.current.corridor) || {};
    const corridorColor = new THREE.Color(corridorCfg.colorCruise || ROUTE_CORRIDOR_COLOR);
    const takeoffColor = new THREE.Color(corridorCfg.colorTakeoff || ROUTE_TAKEOFF_COLOR);
    const landColor = new THREE.Color(corridorCfg.colorLand || ROUTE_LAND_COLOR);
    const dropColor = new THREE.Color(ROUTE_DROP_COLOR);
    const edgeColor = new THREE.Color(ROUTE_CORRIDOR_EDGE_COLOR);
    // Draw the tube at the ENFORCED horizontal tolerance so the visible corridor
    // is the real boundary the geofence checks against (not a tighter cosmetic pipe).
    // Circular by choice: the vertical tolerance (maxVerticalDeviationMeters) is
    // not squashed in, so a level leg is drawn as tall as it is wide.
    const tunnelHalfWidth = Number.isFinite(corridorCfg.maxHorizontalDeviationMeters)
      ? corridorCfg.maxHorizontalDeviationMeters
      : ROUTE_TUNNEL_HALF_WIDTH_METERS;
    // A cruise leg inside one altitude layer takes that layer's colour (when
    // layers are on); a leg climbing or descending between layers stays blue.
    const layerColorOfLeg = (a, b) => {
      if (!corridorCfg.layersEnabled) return null;
      const layer = layerOf(a.agl, corridorCfg);
      return layer && layer === layerOf(b.agl, corridorCfg) ? new THREE.Color(layerColor(layer, corridorCfg.layerCount)) : null;
    };

    // Shared origin = first point of the first route.
    const o = resolvedRoutes[0].pts[0];
    originMerc = mapboxgl.MercatorCoordinate.fromLngLat([o.lon, o.lat], o.alt);
    originScale = originMerc.meterInMercatorCoordinateUnits();

    try {
    resolvedRoutes.forEach(({ pts, active, isTransition, color }) => {
      // Active (flying) corridor is bright/opaque; saved previews are dimmer; transition corridor is distinct amber.
      const wallOpacity = isTransition
        ? 0.75
        : (active
          ? (Number.isFinite(corridorCfg.opacityActive) ? corridorCfg.opacityActive : ROUTE_TUNNEL_OPACITY_ACTIVE)
          : (Number.isFinite(corridorCfg.opacityPreview) ? corridorCfg.opacityPreview : ROUTE_TUNNEL_OPACITY_PREVIEW));
      const edgeOpacity = isTransition ? 0.95 : (active ? 0.95 : 0.55);
      const activeCorridorColor = isTransition ? new THREE.Color(color || ROUTE_TRANSITION_COLOR) : corridorColor;

      const v = pts.map((p) => {
        const vec = toLocal(p.lat, p.lon, p.alt);
        vec.isWaypoint = !p.synthetic;
        return vec;
      });

      // 1) Corridor: one tube per run of legs in the same flight phase and
      //    colour (utils/flightPath.js legPhases). A leg is one colour end to
      //    end; the takeoff climb, a delivery drop and the landing are separate
      //    see-through ringed columns (an arrow on takeoff and landing), so they
      //    never read as part of the route the drone cruises.
      const phases = isTransition ? [] : legPhases(pts);
      const runs = [];
      for (let i = 0; i < v.length - 1; i++) {
        const phase = isTransition ? 'cruise' : phases[i];
        const color = isTransition ? activeCorridorColor
          : phase === 'takeoff' ? takeoffColor
            : phase === 'land' ? landColor
              : phase === 'drop' ? dropColor
                : (layerColorOfLeg(pts[i], pts[i + 1]) || activeCorridorColor);
        const last = runs[runs.length - 1];
        if (last && last.phase === phase && last.color.equals(color)) last.points.push(v[i + 1]);
        else runs.push({ phase, color, points: [v[i], v[i + 1]] });
      }

      runs.forEach((run) => {
        // Filter out duplicate or extremely close consecutive points (zero-length legs).
        const curvePoints = [];
        for (let i = 0; i < run.points.length; i++) {
          if (i === 0 || run.points[i].distanceTo(curvePoints[curvePoints.length - 1]) > 0.2) {
            curvePoints.push(run.points[i]);
          }
        }
        if (curvePoints.length < 2) return;
        const column = run.phase !== 'cruise';

        const points = [];
        const filletRadius = 2.5; // Fillet radius in meters (matches waypoint sphere radius)
        let currentStart = curvePoints[0];
        for (let i = 1; i < curvePoints.length - 1; i++) {
          const prev = curvePoints[i - 1];
          const curr = curvePoints[i];
          const next = curvePoints[i + 1];
          const dirIn = new THREE.Vector3().subVectors(prev, curr).normalize();
          const dirOut = new THREE.Vector3().subVectors(next, curr).normalize();

          // Fillet corners only at actual waypoint turns (heading changes > 18°).
          // Intermediate terrain samples along cruise legs have dirIn.dot(dirOut) ≈ -1.0
          // and must not create artificial Bezier clusters (which bunch up rings).
          const isCorner = !!curr.isWaypoint && dirIn.dot(dirOut) > -0.95;

          // Cap the fillet radius at 40% of the segment lengths
          const d = isCorner ? Math.min(filletRadius, prev.distanceTo(curr) * 0.4, next.distanceTo(curr) * 0.4) : 0;
          const end = d > 0.5 ? curr.clone().addScaledVector(dirIn, d) : curr;

          // Straight points every 5 m up to the corner…
          const distToEnd = currentStart.distanceTo(end);
          const n = Math.max(1, Math.round(distToEnd / 5));
          for (let k = 0; k < n; k++) points.push(new THREE.Vector3().lerpVectors(currentStart, end, k / n));

          if (d > 0.5) {
            // …then a rounded corner (Bezier, 4 segments).
            const bOut = curr.clone().addScaledVector(dirOut, d);
            const bezier = new THREE.QuadraticBezierCurve3(end, curr, bOut);
            for (let k = 0; k < 4; k++) points.push(bezier.getPoint(k / 4));
            currentStart = bOut;
          } else {
            currentStart = curr;
          }
        }
        const lastPoint = curvePoints[curvePoints.length - 1];
        const distToLast = currentStart.distanceTo(lastPoint);
        const nFinal = Math.max(1, Math.round(distToLast / 5));
        for (let k = 0; k <= nFinal; k++) points.push(new THREE.Vector3().lerpVectors(currentStart, lastPoint, k / nFinal));

        const radialSegments = 32; // Smooth circular pipe profile
        const tubularSegments = points.length - 1; // 1-to-1 with the points, no sampling distortion
        const tubeGeo = new THREE.TubeGeometry(new CustomPathCurve(points), tubularSegments, tunnelHalfWidth, radialSegments, false);

        // Semi-transparent walls; columns lighter than the cruise tube.
        const wallMat = new THREE.MeshBasicMaterial({
          color: run.color,
          transparent: true,
          opacity: column ? wallOpacity * 0.5 : wallOpacity,
          side: THREE.DoubleSide,
          depthWrite: false,
        });
        const walls = new THREE.Mesh(tubeGeo, wallMat);
        walls.frustumCulled = false;
        walls.renderOrder = 1;
        group.add(walls);

        // Circular rings show partitions (no longitudinal lines); a column is
        // ringed densely so it reads as a shaft, not a pipe.
        const ringStep = column ? 1 : 3;
        const gridPositions = [];
        const pos = tubeGeo.attributes.position;
        const verticesPerRing = radialSegments + 1;
        const ring = (i) => {
          for (let j = 0; j < radialSegments; j++) {
            const idx1 = i * verticesPerRing + j;
            const idx2 = i * verticesPerRing + (j + 1);
            gridPositions.push(pos.getX(idx1), pos.getY(idx1), pos.getZ(idx1), pos.getX(idx2), pos.getY(idx2), pos.getZ(idx2));
          }
        };
        for (let i = 0; i <= tubularSegments; i += ringStep) ring(i);
        if (tubularSegments % ringStep !== 0) ring(tubularSegments); // close the end cap outline
        const gridGeo = new THREE.BufferGeometry();
        gridGeo.setAttribute('position', new THREE.Float32BufferAttribute(gridPositions, 3));
        const gridMat = new THREE.LineBasicMaterial({ color: run.color, transparent: true, opacity: edgeOpacity * 0.75 });
        const edges = new THREE.LineSegments(gridGeo, gridMat);
        edges.frustumCulled = false;
        group.add(edges);
        disposables.push(tubeGeo, wallMat, gridGeo, gridMat);

        // Direction arrow: up over the takeoff climb, down at the landing.
        if (run.phase === 'takeoff' || run.phase === 'land') {
          const coneRadius = Math.min(tunnelHalfWidth * 0.5, 4);
          const coneGeo = new THREE.ConeGeometry(coneRadius, coneRadius * 1.6, 24);
          const coneMat = new THREE.MeshBasicMaterial({ color: run.color, transparent: true, opacity: Math.min(1, edgeOpacity) });
          const cone = new THREE.Mesh(coneGeo, coneMat);
          const up = run.phase === 'takeoff';
          // Cones point along +Y; the scene's "up" is +Z.
          cone.rotation.x = up ? Math.PI / 2 : -Math.PI / 2;
          // At the top of the climb pointing up; just above the touchdown point pointing down.
          cone.position.copy(curvePoints[curvePoints.length - 1]).add(new THREE.Vector3(0, 0, coneRadius * 1.6));
          cone.frustumCulled = false;
          group.add(cone);
          disposables.push(coneGeo, coneMat);
        }
      });

        // 2) Red dashed flight-path line through the corridor (one instanced
        //    mesh per route -> a single draw call regardless of dash count).
        let totalLen = 0;
        for (let i = 0; i < v.length - 1; i += 1) totalLen += v[i].distanceTo(v[i + 1]);
        if (totalLen > 2) {
          let unit = ROUTE_DASH_UNIT_METERS;
          if (totalLen / unit > ROUTE_DASH_MAX_PER_ROUTE) unit = totalLen / ROUTE_DASH_MAX_PER_ROUTE;
          const dashLen = unit * 0.5;

          const xAxis = new THREE.Vector3(1, 0, 0);
          const dashMatrices = [];
          for (let i = 0; i < v.length - 1; i += 1) {
            const A = v[i];
            const B = v[i + 1];
            const seg = new THREE.Vector3().subVectors(B, A);
            const len = seg.length();
            if (len < 2) continue;
            const dirN = seg.clone().normalize();
            const q = new THREE.Quaternion().setFromUnitVectors(xAxis, dirN);
            const scale = new THREE.Vector3(dashLen, 1, 1);
            for (let k = 0; ; k += 1) {
              const t = (k + 0.5) * unit;
              if (t > len) break;
              const pos = A.clone().addScaledVector(dirN, t);
              dashMatrices.push(new THREE.Matrix4().compose(pos, q, scale));
            }
          }

          if (dashMatrices.length > 0) {
            const dashGeo = new THREE.BoxGeometry(1, ROUTE_DASH_CROSS_SECTION_METERS, ROUTE_DASH_CROSS_SECTION_METERS);
            const dashMat = new THREE.MeshBasicMaterial({
              color: isTransition ? ROUTE_TRANSITION_DASH_COLOR : ROUTE_PATH_DASH_COLOR,
              transparent: true,
              opacity: active ? 0.75 : 0.5,
            });
            const dashes = new THREE.InstancedMesh(dashGeo, dashMat, dashMatrices.length);
            dashMatrices.forEach((m, i) => dashes.setMatrixAt(i, m));
            dashes.instanceMatrix.needsUpdate = true;
            dashes.frustumCulled = false;
            group.add(dashes);
            disposables.push(dashGeo, dashMat);
          }
        }

      // Waypoint node sphere at altitude (no ground drop-pole).
      const sphereGeo = new THREE.SphereGeometry(ROUTE_WAYPOINT_SPHERE_RADIUS_METERS, 12, 12);
      const sphereColor = isTransition ? activeCorridorColor : edgeColor;
      const sphereMat = new THREE.MeshBasicMaterial({ color: sphereColor });
      disposables.push(sphereGeo, sphereMat);

      pts.forEach((p) => {
        if (p.synthetic) return; // climb/descent helper points carry dashes only
        const sphere = new THREE.Mesh(sphereGeo, sphereMat);
        sphere.position.copy(toLocal(p.lat, p.lon, p.alt));
        sphere.frustumCulled = false;
        group.add(sphere);
      });
    });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[UTMS route] corridor build failed:', err);
    }
  };

  // Rebuilt when a trail grows, and with the route geometry (whose origin it shares).
  // Same height convention as the tube: ground under the point + height above home.
  const rebuildTrails = (trails) => {
    trailGroup.clear();
    trailDisposables.forEach((d) => { try { d.dispose(); } catch { /* already gone */ } });
    trailDisposables.length = 0;
    if (!originMerc) return;
    Object.values(trails.byDrone || {}).forEach(({ pts }) => {
      if (!pts || pts.length < 2) return;
      const positions = [];
      pts.forEach((p) => {
        const ground = queryGround(p.lat, p.lon);
        const at = toLocal(p.lat, p.lon, (ground ?? 0) + p.agl);
        positions.push(at.x, at.y, at.z);
      });
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
      const mat = new THREE.LineBasicMaterial({ color: ROUTE_FLOWN_TRAIL_COLOR, transparent: true, opacity: 0.9 });
      const line = new THREE.Line(geo, mat);
      line.frustumCulled = false;
      line.renderOrder = 2;
      trailGroup.add(line);
      trailDisposables.push(geo, mat);
    });
  };

  return {
    id: ROUTE_RIBBON_LAYER_ID,
    type: 'custom',
    renderingMode: '3d',
    onAdd: (map, gl) => {
      mapInstance = map;
      camera = new THREE.Camera();
      scene = new THREE.Scene();
      scene.add(group);
      scene.add(trailGroup);
      renderer = new THREE.WebGLRenderer({ canvas: map.getCanvas(), context: gl, antialias: true, powerPreference: 'high-performance' });
      renderer.autoClear = false;
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      builtVersion = -1;
    },
    render: (gl, matrix) => {
      if (disposed || !renderer || !camera || !scene || !mapInstance) return;

      const data = routeDataRef.current || { version: 0, routes: [] };
      // Rebuild on new data, OR when terrain under the route finally loads/changes
      // (queryTerrainElevation returns 0 until DEM tiles arrive — this self-heals
      // the altitude so the tunnel rises to the real flight height).
      let needsRebuild = data.version !== builtVersion;
      // Terrain drift self-heal: re-resolve altitude as DEM tiles stream in.
      // Throttled — querying terrain for every waypoint of every route on every
      // frame is wasteful at 60fps; ~4x/sec heals fast enough to be invisible.
      const nowMs = (typeof performance !== 'undefined' ? performance.now() : Date.now());
      if (!needsRebuild && resolvedRoutesList && nowMs - lastTerrainScanMs > TERRAIN_RECHECK_INTERVAL_MS) {
        lastTerrainScanMs = nowMs;
        for (let r = 0; r < resolvedRoutesList.length; r++) {
          const route = resolvedRoutesList[r];
          for (let p = 0; p < route.pts.length; p++) {
            const pt = route.pts[p];
            const currentGround = queryGround(pt.lat, pt.lon);
            if (currentGround !== null && pt.groundAlt !== undefined) {
              if (Math.abs(currentGround - pt.groundAlt) > 0.5) {
                needsRebuild = true;
                break;
              }
            }
          }
          if (needsRebuild) break;
        }
      }
      if (needsRebuild) {
        rebuildGeometry();
        builtVersion = data.version;
      }
      const trails = trailsRef && trailsRef.current;
      if (trails && (needsRebuild || trails.version !== builtTrailVersion)) {
        rebuildTrails(trails);
        builtTrailVersion = trails.version;
      }
      if (!originMerc || group.children.length === 0) return;

      // Place the metres-scaled geometry: translate to the origin and scale
      // metres -> mercator units (matches the working drone-model layer).
      const local = new THREE.Matrix4()
        .makeTranslation(originMerc.x, originMerc.y, originMerc.z)
        .scale(new THREE.Vector3(originScale, originScale, originScale));
      camera.projectionMatrix.copy(new THREE.Matrix4().fromArray(matrix).multiply(local));

      renderer.resetState();
      renderer.clearDepth();
      renderer.render(scene, camera);
    },
    onRemove: () => {
      disposed = true;
      clearGroup();
      trailGroup.clear();
      trailDisposables.forEach((d) => { try { d.dispose(); } catch { /* already gone */ } });
      if (scene) scene.clear();
      if (renderer) renderer.dispose();
      camera = null;
      scene = null;
      renderer = null;
      mapInstance = null;
    },
  };
}

function Map3DRenderer({
  location,
  dronesTelemetry = {},
  activeMissions = {},
  localRoutes = null,
  drones = [],
  currentUserRole = 'operator',
  onDroneSelect = null,
  styleKey = 'NavigationPreviewNight',
  cameraMode = 'third_person',
  followDroneId = null,
  onFollowDroneChange = null,
  show3DBuildings = true,
  isActive = true,
  topOffset = 0,
  isMobileLayout = false,
  sidebarVisible = true,
  stitchChrome = false,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef({});
  const prevDronePositionsRef = useRef({});
  const posesRef = useRef({});
  const modelUrlByDroneRef = useRef({});
  const vehicleModelByDroneRef = useRef({});
  const requiredModelUrlsRef = useRef(new Set());
  const activePoseRef = useRef(null);
  const activeRoutesRef = useRef({});
  const trailsRef = useRef({ version: 0, byDrone: {} }); // flown trail per drone, this mission
  const deliveryDropsRef = useRef({});  // droneId -> { missionId, ropeLength, dropHeight, drops:[{lat,lon,key}] } for active delivery missions
  // Winch progress lives here, above the map layer, so it survives every layer
  // rebuild (style load, WebGL context restore, returning from the 2D view).
  const winchStateRef = useRef(new Map());
  const groundParcelsRef = useRef(new Map());
  const configRef = useRef(getCachedOpsConfig());  // shared ops config (winch arm, corridor width/colors/time) — read live in the render loop
  const [corridorInfo, setCorridorInfo] = useState([]); // active corridors for the countdown overlay
  // Waypoints of each drone's RUNNING mission, kept so the pipeline overlay can
  // name the current phase. Stashed here rather than refetched: the route effect
  // deliberately does not re-run on progress ticks, but progress is exactly what
  // moves the pipeline, so the phase is derived in a memo instead.
  const [activeMissionWps, setActiveMissionWps] = useState({});
  const [, setCorridorTick] = useState(0);              // 1s heartbeat to refresh the countdown
  const show3DBuildingsRef = useRef(show3DBuildings);
  const appliedStyleUrlRef = useRef(null);
  const initialStyleUrlRef = useRef(MAPBOX_STYLE_URLS[styleKey] || MAPBOX_STYLE_URLS.NavigationPreviewNight);
  const initialCenterRef = useRef({
    lat: Number(location?.lat) || 21.1458,
    lon: Number(location?.lon) || 79.0882,
  });
  const programmaticMoveRef = useRef(false);
  const programmaticMoveIdRef = useRef(0);
  const modelRetryTimersRef = useRef({});
  const modelRetryDelaysRef = useRef({});
  const modelStatusRef = useRef({});
  const [cameraFollowing, setCameraFollowing] = useState(true);
  const cameraReengageTimerRef = useRef(null);
  const smoothCamPosRef = useRef(null);
  const smoothCamLookRef = useRef(null);
  const smoothLookAltRef = useRef(null);
  const smoothCameraHeadingRef = useRef(null);
  const cameraAnimFrameRef = useRef(null);
  const userZoomFactorRef = useRef(1.0);
  const userOrbitOffsetRef = useRef(0);
  const orbitDragStateRef = useRef({ active: false, lastX: 0 });
  const followHandlersRef = useRef({});
  const [modelReady, setModelReady] = useState(() => hasCachedModelTemplate(DRONE_MODEL_URL));
  const [modelFailed, setModelFailed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [geoZones, setGeoZones] = useState(null);
  const [selectedZones, setSelectedZones] = useState(null); // null = all zones visible (uninitialised)
  const [zoneFilterOpen, setZoneFilterOpen] = useState(false);
  const selectedZonesRef = useRef(null);
  selectedZonesRef.current = selectedZones;
  const [missionRefreshTick, setMissionRefreshTick] = useState(0);

  // Mirror the latest activeMissions for reads inside the route effect, but only
  // RE-RUN that effect when the set of active mission IDs changes (not on every
  // progress tick) — avoids refetching/rebuilding routes ~2x/sec during a flight.
  const activeMissionsRef = useRef(activeMissions);
  activeMissionsRef.current = activeMissions;
  const dronesTelemetryRef = useRef(dronesTelemetry);
  dronesTelemetryRef.current = dronesTelemetry;
  const activeMissionIdsKey = useMemo(
    () => drones.map((d) => {
      const am = activeMissions?.[d.id];
      // Include state + start + duration so the corridor rebuilds when a mission
      // starts/ends (state) or its server-side time window arrives — but NOT on
      // every progress tick (progress is deliberately excluded).
      return `${d.id}:${am?.missionId || ''}:${am?.state || ''}:${am?.startedAt || ''}:${am?.durationSec || ''}`;
    }).join('|'),
    [drones, activeMissions]
  );
  // Mission pipeline for the overlay: which phase each flying drone is on right
  // now. Derived from activeMissions (updates every progress tick) crossed with
  // the waypoints stashed by the route effect (updates only when a mission
  // starts/ends), so the phase advances without refetching a thing.
  const missionPipeline = useMemo(() => {
    return drones.map((d) => {
      const am = activeMissions?.[d.id];
      if (!am || !ACTIVE_MISSION_STATES.has(am.state)) return null;
      const stage = currentStage(activeMissionWps[d.id], am.currentWaypoint);
      if (!stage) return null;
      return {
        droneId: d.id,
        name: d.name || d.id,
        state: am.state,
        progress: Math.max(0, Math.min(100, Number(am.progress) || 0)),
        stage,
      };
    }).filter(Boolean);
  }, [drones, activeMissions, activeMissionWps]);

  const flightTrailRef = useRef({});
  const routeDataRef = useRef({ version: 0, routes: [] });
  const show2DMarkers = false;
  const showMissionRoutes = true;
  const showSafetyRadius = false;

  useEffect(() => {
    getCachedDroneModelTemplate(DRONE_MODEL_URL).catch(() => {
      // Retry path is handled by the runtime loader callbacks.
    });
  }, []);

  // Load the shared ops config (winch arm thresholds, corridor width/colors/time)
  // and keep configRef live so the render loop reads current values, plus refresh
  // when an admin saves it elsewhere.
  useEffect(() => {
    let alive = true;
    loadOpsConfig().then((cfg) => { if (alive) { configRef.current = cfg; setCorridorTick((t) => t + 1); } });
    const onChange = (e) => {
      configRef.current = (e && e.detail) || getCachedOpsConfig();
      // Bump the route version so corridor geometry (width/colors) rebuilds.
      routeDataRef.current = { version: routeDataRef.current.version + 1, routes: routeDataRef.current.routes };
      if (mapRef.current && mapRef.current.triggerRepaint) mapRef.current.triggerRepaint();
      setCorridorTick((t) => t + 1);
    };
    window.addEventListener(OPS_CONFIG_EVENT, onChange);
    return () => { alive = false; window.removeEventListener(OPS_CONFIG_EVENT, onChange); };
  }, []);

  // 1s heartbeat: tick the corridor countdown overlay. The tube itself is drawn/
  // dropped by mission state (see the route effect), not by this timer.
  useEffect(() => {
    if (!corridorInfo.length) return undefined;
    const id = setInterval(() => setCorridorTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [corridorInfo]);

  // Load no-fly zone GeoJSON once on mount
  useEffect(() => {
    let cancelled = false;
    fetch(ZONE_GEOJSON_URL)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (!cancelled) setGeoZones(data);
      })
      .catch(() => { /* zones optional — silent fail */ });
    return () => { cancelled = true; };
  }, []);

  // Refresh route previews the moment a mission is created/saved/deleted, so a
  // newly planned mission appears in 3D without re-selecting the drone.
  useEffect(() => {
    const onMissionChanged = () => setMissionRefreshTick((t) => t + 1);
    window.addEventListener('utms:mission-saved', onMissionChanged);
    return () => window.removeEventListener('utms:mission-saved', onMissionChanged);
  }, []);

  useEffect(() => {
    show3DBuildingsRef.current = show3DBuildings;
  }, [show3DBuildings]);

  const runProgrammaticEase = useCallback((map, options) => {
    programmaticMoveIdRef.current += 1;
    const moveId = programmaticMoveIdRef.current;
    programmaticMoveRef.current = true;
    map.easeTo(options);
    map.once('moveend', () => {
      if (programmaticMoveIdRef.current === moveId) {
        programmaticMoveRef.current = false;
      }
    });
  }, []);

  const visibleDrones = useMemo(() => {
    // Other operators' aircraft in the air, under their daily handle (the
    // server sends nothing else about them): grey, not selectable.
    const own = new Set(drones.map((d) => d.id));
    const shared = Object.entries(dronesTelemetry)
      .filter(([id, tel]) => tel?.readonly && !own.has(id))
      .map(([id, tel]) => ({ id, name: tel.droneName || id, vehicleModel: tel.vehicleModel, readonly: true }));

    return drones
      .filter((drone) => {
        const tel = dronesTelemetry[drone.id] || drone.telemetry;
        const mode = String(tel?.mode || '').trim().toUpperCase();
        return !!tel?.connected && !tel?.stale && !!mode && mode !== 'N/A' && mode !== 'DISCONNECTED';
      })
      .map((drone) => ({ ...drone, readonly: false }))
      .concat(shared);
  }, [drones, dronesTelemetry]);

  const trackedDrones = useMemo(() => {
    return visibleDrones.map((drone) => {
      const telemetry = dronesTelemetry[drone.id];
      const vehicleModel = telemetry?.vehicleModel || drone.vehicleModel || 'quadcopter';
      return {
        ...drone,
        vehicleModel
      };
    }).filter((drone) => {
      const telemetry = dronesTelemetry[drone.id];
      const lat = Number(telemetry?.lat);
      const lon = Number(telemetry?.lon);
      return !!telemetry?.connected && hasValidCoordinates(lat, lon);
    });
  }, [visibleDrones, dronesTelemetry]);

  const activeDroneId = useMemo(() => {
    if (followDroneId && trackedDrones.some((drone) => drone.id === followDroneId)) {
      return followDroneId;
    }
    // The camera never settles on another operator's aircraft by default.
    return trackedDrones.find((drone) => !drone.readonly)?.id || null;
  }, [followDroneId, trackedDrones]);

  const modelUrlByDrone = useMemo(() => {
    const mapping = {};
    trackedDrones.forEach((drone) => {
      mapping[drone.id] = resolveVehicleModelUrl(drone.vehicleModel);
    });
    return mapping;
  }, [trackedDrones]);

  const vehicleModelByDrone = useMemo(() => {
    const mapping = {};
    trackedDrones.forEach((drone) => {
      mapping[drone.id] = drone.vehicleModel || '';
    });
    return mapping;
  }, [trackedDrones]);

  const requiredModelUrls = useMemo(() => {
    return new Set(Object.values(modelUrlByDrone));
  }, [modelUrlByDrone]);

  const styleUrl = MAPBOX_STYLE_URLS[styleKey] || MAPBOX_STYLE_URLS.NavigationPreviewNight;

  const deriveDronePose = useCallback((droneId, telemetry) => {
    const lat = Number(telemetry?.lat);
    const lon = Number(telemetry?.lon);

    if (!hasValidCoordinates(lat, lon)) {
      return null;
    }

    const lastPos = prevDronePositionsRef.current[droneId] || null;
    const heading = getEffectiveHeading(telemetry, lastPos);
    prevDronePositionsRef.current[droneId] = { lat, lon };

    const parsedRelativeAlt = parseFloat(telemetry?.relative_alt);
    const parsedAlt = parseFloat(telemetry?.alt);
    const hasRelativeAlt = Number.isFinite(parsedRelativeAlt);
    const hasAbsoluteAlt = Number.isFinite(parsedAlt);
    const relativeAltitudeMeters = hasRelativeAlt
      ? Math.max(0, normalizeAltitudeMeters(parsedRelativeAlt))
      : null;
    const absoluteAltitudeMeters = hasAbsoluteAlt
      ? normalizeAltitudeMeters(parsedAlt)
      : null;

    // Real attitude from the flight controller (MAVLink ATTITUDE, radians).
    const pitchRad = parseFloat(telemetry?.pitch);
    const rollRad = parseFloat(telemetry?.roll);
    const speedMps = parseFloat(telemetry?.speed ?? telemetry?.groundspeed ?? 0);

    return {
      lat,
      lon,
      heading: Number.isFinite(heading) ? heading : 0,
      altitudeAgl: relativeAltitudeMeters,
      altitudeMsl: absoluteAltitudeMeters,
      pitch: Number.isFinite(pitchRad) ? pitchRad : 0,
      roll: Number.isFinite(rollRad) ? rollRad : 0,
      speed: Number.isFinite(speedMps) ? speedMps : 0,
      // Blades only turn once the FC is armed. A disarmed aircraft with spinning
      // props reads as "motors live" on the one display the operator trusts.
      armed: !!telemetry?.armed,
    };
  }, []);

  const updateModelReadyState = useCallback((urls = requiredModelUrlsRef.current) => {
    if (!urls || urls.size === 0) {
      setModelReady(false);
      setModelFailed(false);
      return;
    }

    let allReady = true;
    let anyFailed = false;

    urls.forEach((url) => {
      const status = modelStatusRef.current[url]?.status;
      if (status === 'failed') {
        anyFailed = true;
      }
      if (status !== 'ready') {
        allReady = false;
      }
    });

    setModelReady(allReady);
    setModelFailed(anyFailed);
  }, []);

  const loadModelTemplate = useCallback((modelUrl) => {
    return getCachedDroneModelTemplate(modelUrl)
      .then(() => {
        modelStatusRef.current[modelUrl] = { status: 'ready' };
        updateModelReadyState();
      })
      .catch((error) => {
        modelStatusRef.current[modelUrl] = { status: 'failed', error };
        updateModelReadyState();
        throw error;
      });
  }, [updateModelReadyState]);

  const scheduleModelRetry = useCallback((modelUrl) => {
    if (modelRetryTimersRef.current[modelUrl]) {
      return;
    }

    const delay = modelRetryDelaysRef.current[modelUrl] || MODEL_RETRY_BASE_DELAY_MS;
    modelRetryTimersRef.current[modelUrl] = window.setTimeout(() => {
      delete modelRetryTimersRef.current[modelUrl];
      if (!requiredModelUrlsRef.current.has(modelUrl)) {
        return;
      }
      const nextDelay = Math.min(Math.floor(delay * 1.5), MODEL_RETRY_MAX_DELAY_MS);
      modelRetryDelaysRef.current[modelUrl] = nextDelay;
      loadModelTemplate(modelUrl).catch(() => {
        scheduleModelRetry(modelUrl);
      });
    }, delay);
  }, [loadModelTemplate]);

  const handleModelLoaded = useCallback((modelUrl) => {
    modelStatusRef.current[modelUrl] = { status: 'ready' };
    updateModelReadyState();
  }, [updateModelReadyState]);

  const handleModelError = useCallback((modelUrl, error) => {
    console.error('Failed to load drone model:', modelUrl, error);
    modelStatusRef.current[modelUrl] = { status: 'failed', error };
    updateModelReadyState();
    if (requiredModelUrlsRef.current.has(modelUrl)) {
      scheduleModelRetry(modelUrl);
    }
  }, [scheduleModelRetry, updateModelReadyState]);

  useEffect(() => {
    modelUrlByDroneRef.current = modelUrlByDrone;
    requiredModelUrlsRef.current = requiredModelUrls;
    vehicleModelByDroneRef.current = vehicleModelByDrone;

    Object.keys(modelRetryTimersRef.current).forEach((url) => {
      if (!requiredModelUrls.has(url)) {
        window.clearTimeout(modelRetryTimersRef.current[url]);
        delete modelRetryTimersRef.current[url];
        delete modelRetryDelaysRef.current[url];
      }
    });

    if (requiredModelUrls.size === 0) {
      updateModelReadyState(requiredModelUrls);
      return;
    }

    requiredModelUrls.forEach((url) => {
      if (hasCachedModelTemplate(url)) {
        if (modelStatusRef.current[url]?.status !== 'ready') {
          modelStatusRef.current[url] = { status: 'ready' };
        }
        return;
      }

      if (!modelStatusRef.current[url] || modelStatusRef.current[url].status === 'failed') {
        modelStatusRef.current[url] = { status: 'loading' };
        loadModelTemplate(url).catch(() => {
          scheduleModelRetry(url);
        });
      }
    });

    updateModelReadyState(requiredModelUrls);
  }, [modelUrlByDrone, vehicleModelByDrone, requiredModelUrls, loadModelTemplate, scheduleModelRetry, updateModelReadyState]);

  const addDroneModelLayer = useCallback((map) => {
    // Same rationale as addRouteRibbonLayer: isStyleLoaded() is false during
    // ordinary tile work and silently dropped the layer (drone model randomly
    // missing until a style switch). try/catch covers real style swaps.
    if (!map || map.getLayer(DRONE_MODEL_LAYER_ID)) {
      return;
    }

    if (!requiredModelUrlsRef.current || requiredModelUrlsRef.current.size === 0) {
      return;
    }

    try {
      map.addLayer(
        createDroneModelLayer({
          posesRef,
          modelUrlByDroneRef,
          vehicleModelByDroneRef,
          onModelLoaded: handleModelLoaded,
          onModelError: handleModelError,
          activeRoutesRef,
          deliveryDropsRef,
          winchStateRef,
          groundParcelsRef,
          configRef,
        })
      );
      ensureDroneLayerOnTop(map);
    } catch { /* style mid-swap; re-added on style.load */ }
  }, [handleModelError, handleModelLoaded]);

  const addRouteRibbonLayer = useCallback((map) => {
    // NOTE: no isStyleLoaded() guard — it reports false during ordinary tile/
    // source work (e.g. right after our own addSource calls), which silently
    // dropped the layer. addLayer only truly fails mid style-swap; the catch
    // covers that and the style.load handler re-adds it.
    if (!map || map.getLayer(ROUTE_RIBBON_LAYER_ID)) {
      return;
    }
    try {
      // Keep the corridor just below the drone model so the drone renders on top.
      const beforeId = map.getLayer(DRONE_MODEL_LAYER_ID) ? DRONE_MODEL_LAYER_ID : undefined;
      map.addLayer(createRouteRibbonLayer({ routeDataRef, activeRoutesRef, configRef, trailsRef }), beforeId);
    } catch { /* style mid-swap; re-added on style.load */ }
  }, []);

  useEffect(() => {
    if (!onFollowDroneChange) return;
    if (activeDroneId && activeDroneId !== followDroneId) {
      onFollowDroneChange(activeDroneId);
    }
  }, [activeDroneId, followDroneId, onFollowDroneChange]);

  useEffect(() => {
    const map = mapRef.current;
    const styleReady = !!map?.isStyleLoaded?.();
    const hasBuildings = !!(styleReady && show3DBuildingsRef.current && map?.getLayer?.('utms-3d-buildings'));
    const nextPoses = {};

    trackedDrones.forEach((drone) => {
      const telemetry = dronesTelemetry[drone.id] || drone.telemetry;
      const pose = deriveDronePose(drone.id, telemetry);
      if (!pose) return;

      if (hasBuildings && map?.queryTerrainElevation) {
        const terrainElevation = map.queryTerrainElevation([pose.lon, pose.lat], TERRAIN_QUERY_OPTIONS);
        const terrainMeters = Number.isFinite(terrainElevation) ? terrainElevation : 0;
        pose.localBuildingTopMsl = resolveLocalBuildingTopMeters(map, pose.lat, pose.lon, terrainMeters);
      }

      nextPoses[drone.id] = pose;
    });

    // Clean up stale entries from prevDronePositionsRef to prevent memory leak
    const activeIds = new Set(trackedDrones.map((d) => d.id));
    Object.keys(prevDronePositionsRef.current).forEach((id) => {
      if (!activeIds.has(id)) {
        delete prevDronePositionsRef.current[id];
      }
    });

    posesRef.current = nextPoses;
    activePoseRef.current = activeDroneId ? nextPoses[activeDroneId] || null : null;

    if (map && Object.keys(nextPoses).length > 0) {
      map.triggerRepaint();
    }
  }, [activeDroneId, trackedDrones, dronesTelemetry, deriveDronePose, show3DBuildings]);

  // Continuous render loop so the drone model GLIDES smoothly between the ~2 Hz
  // telemetry fixes. The custom layer otherwise only repaints on each fix (and
  // until the smoothing settles), so the model moves in stutters ("atak-atak").
  // We repaint every frame while a drone is tracked; the layer's per-frame lerp
  // does the actual smoothing. (Mapbox coalesces repaint requests, so this is
  // safe alongside the camera-follow loop.)
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isActive || trackedDrones.length === 0) return undefined;
    let raf = requestAnimationFrame(function tick() {
      map.triggerRepaint();
      raf = requestAnimationFrame(tick);
    });
    return () => cancelAnimationFrame(raf);
  }, [isActive, trackedDrones.length]);

  useEffect(() => {
    if (!containerRef.current || mapRef.current || !MAPBOX_TOKEN) {
      return;
    }

    mapboxgl.accessToken = MAPBOX_TOKEN;
    const initialLat = initialCenterRef.current.lat;
    const initialLon = initialCenterRef.current.lon;

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: initialStyleUrlRef.current,
      center: [initialLon, initialLat],
      zoom: 12,
      pitch: 58,
      bearing: 0,
      antialias: true,
      attributionControl: false,
      canvasContextAttributes: {
        antialias: true,
        powerPreference: 'high-performance',
      },
    });
    // Left edge, not bottom-right: that corner is the voice agent's, and CSS
    // centres this container vertically to sit with the recenter button.
    map.addControl(new mapboxgl.NavigationControl({ showCompass: true, visualizePitch: true }), 'bottom-left');

    // Custom Compass Toggle: Clicking it when flat restores beautiful 3D pitch!
    let compassBtn = null;
    const handleCompassClick = (e) => {
      const currentPitch = map.getPitch();
      const currentBearing = map.getBearing();
      if (Math.abs(currentPitch) < 1 && Math.abs(currentBearing) < 1) {
        e.stopPropagation();
        e.preventDefault();
        map.easeTo({
          pitch: 58,
          bearing: 0,
          duration: 800,
          essential: true
        });
      }
    };

    const compassTimer = setTimeout(() => {
      compassBtn = containerRef.current?.querySelector('.mapboxgl-ctrl-compass');
      if (compassBtn) {
        compassBtn.addEventListener('click', handleCompassClick);
      }
    }, 100);

    // Any user-initiated interaction breaks follow, giving complete free map access
    const handleUserInteraction = (e) => {
      if (!e || !e.originalEvent) return;
      setCameraFollowing(false);
      if (cameraReengageTimerRef.current) {
        clearTimeout(cameraReengageTimerRef.current);
        cameraReengageTimerRef.current = null;
      }
    };
    map.on('dragstart', handleUserInteraction);
    map.on('zoomstart', handleUserInteraction);
    map.on('rotatestart', handleUserInteraction);
    map.on('pitchstart', handleUserInteraction);

    const container = containerRef.current;

    // Custom scroll-wheel zoom while following: adjusts the chase-cam distance
    // (zoom toward/away from the drone) WITHOUT breaking the follow link. Native
    // scrollZoom is disabled while following (see the scrollZoom effect below),
    // so the wheel never fires `zoomstart` (which would drop follow).
    const handleFollowZoom = (e) => {
      e.preventDefault();
      const zoomIn = e.deltaY < 0;
      const factor = zoomIn ? 0.92 : 1.08;
      userZoomFactorRef.current = Math.max(0.12, Math.min(10.0, userZoomFactorRef.current * factor));
    };

    // Custom right-click drag orbit: adjusts chase cam bearing offset
    const handleOrbitStart = (e) => {
      if (e.button === 2) {
        orbitDragStateRef.current = { active: true, lastX: e.clientX };
        e.preventDefault();
      }
    };
    const handleOrbitMove = (e) => {
      if (!orbitDragStateRef.current.active) return;
      const deltaX = e.clientX - orbitDragStateRef.current.lastX;
      orbitDragStateRef.current.lastX = e.clientX;
      userOrbitOffsetRef.current += deltaX * 0.5;
    };
    const handleOrbitEnd = () => {
      orbitDragStateRef.current.active = false;
    };
    const handleContextMenu = (e) => e.preventDefault();

    if (container) {
      container.addEventListener('wheel', handleFollowZoom, { passive: false });
      container.addEventListener('mousedown', handleOrbitStart);
      container.addEventListener('contextmenu', handleContextMenu);
    }
    window.addEventListener('mousemove', handleOrbitMove);
    window.addEventListener('mouseup', handleOrbitEnd);

    // Store refs for cleanup
    followHandlersRef.current = {
      handleFollowZoom, handleOrbitStart, handleOrbitMove, handleOrbitEnd, handleContextMenu,
      handleUserInteraction, container,
    };

    const handleStyleLoad = () => {
      ensureTerrainAndBuildings(map, show3DBuildingsRef.current);
      updateModelReadyState();
      if (requiredModelUrlsRef.current.size > 0) {
        addDroneModelLayer(map);
      }
      addRouteRibbonLayer(map);
    };

    map.on('style.load', handleStyleLoad);

    // Terrain DEM tiles stream in asynchronously. The route ribbon resolves each
    // waypoint's altitude as `terrainElevation + AGL`, but terrain reads 0/stale
    // until its tile arrives — so a route built too early sits underground until
    // something repaints and its per-frame self-heal re-resolves the altitude.
    // The continuous repaint loop only runs while a drone is tracked, so saved/
    // preview routes (no tracked drone) had no trigger and stayed underground.
    // Repaint whenever DEM tiles settle so the self-heal always fires.
    let demSettleTimer = null;
    const handleDemSourceData = (e) => {
      if (e.sourceId === 'utms-dem' && (e.isSourceLoaded || e.tile)) {
        map.triggerRepaint();
        // The route layer throttles its terrain re-scan, so the immediate repaint
        // above may fall inside that window and skip the heal. Schedule one more
        // repaint just past the throttle interval so the final settled terrain is
        // always picked up (otherwise a fast tile load could leave it stale).
        clearTimeout(demSettleTimer);
        demSettleTimer = setTimeout(() => map.triggerRepaint(), TERRAIN_RECHECK_INTERVAL_MS + 50);
      }
    };
    map.on('sourcedata', handleDemSourceData);

    mapRef.current = map;
    appliedStyleUrlRef.current = initialStyleUrlRef.current;

    if (process.env.NODE_ENV === 'development') {
      // Dev-only handles for automated verification (not present in prod builds).
      window.__utmsMap = map;
      window.__utmsRouteDataRef = routeDataRef;
    }

    const canvas = map.getCanvas();
    const handleContextLost = (event) => {
      event.preventDefault();
      console.warn('Map WebGL context lost.');
    };
    const handleContextRestored = () => {
      console.info('Map WebGL context restored.');
      if (map.getLayer(DRONE_MODEL_LAYER_ID)) {
        map.removeLayer(DRONE_MODEL_LAYER_ID);
      }
      if (map.getLayer(ROUTE_RIBBON_LAYER_ID)) {
        map.removeLayer(ROUTE_RIBBON_LAYER_ID);
      }
      addRouteRibbonLayer(map);
      addDroneModelLayer(map);
      map.triggerRepaint();
    };
    canvas.addEventListener('webglcontextlost', handleContextLost, false);
    canvas.addEventListener('webglcontextrestored', handleContextRestored, false);

    return () => {
      if (compassBtn) {
        compassBtn.removeEventListener('click', handleCompassClick);
      }
      clearTimeout(compassTimer);
      map.off('dragstart', handleUserInteraction);
      map.off('zoomstart', handleUserInteraction);
      map.off('rotatestart', handleUserInteraction);
      map.off('pitchstart', handleUserInteraction);
      if (container) {
        container.removeEventListener('wheel', handleFollowZoom);
        container.removeEventListener('mousedown', handleOrbitStart);
        container.removeEventListener('contextmenu', handleContextMenu);
      }
      window.removeEventListener('mousemove', handleOrbitMove);
      window.removeEventListener('mouseup', handleOrbitEnd);
      if (cameraReengageTimerRef.current) {
        clearTimeout(cameraReengageTimerRef.current);
        cameraReengageTimerRef.current = null;
      }
      if (cameraAnimFrameRef.current) {
        cancelAnimationFrame(cameraAnimFrameRef.current);
        cameraAnimFrameRef.current = null;
      }
      map.off('style.load', handleStyleLoad);
      map.off('sourcedata', handleDemSourceData);
      clearTimeout(demSettleTimer);
      canvas.removeEventListener('webglcontextlost', handleContextLost, false);
      canvas.removeEventListener('webglcontextrestored', handleContextRestored, false);
      Object.values(modelRetryTimersRef.current).forEach((timerId) => {
        window.clearTimeout(timerId);
      });
      modelRetryTimersRef.current = {};
      modelRetryDelaysRef.current = {};
      Object.values(markersRef.current).forEach((marker) => marker.remove());
      markersRef.current = {};

      if (map.getLayer(DRONE_MODEL_LAYER_ID)) {
        map.removeLayer(DRONE_MODEL_LAYER_ID);
      }
      if (map.getLayer(ROUTE_RIBBON_LAYER_ID)) {
        map.removeLayer(ROUTE_RIBBON_LAYER_ID);
      }

      cleanupMapLayers(map, 'utms-zones');
      cleanupMapLayers(map, 'utms-mission-');
      map.remove();
      mapRef.current = null;
      appliedStyleUrlRef.current = null;
    };
  }, [addDroneModelLayer, addRouteRibbonLayer, updateModelReadyState]);

  // While FOLLOWING the drone, disable Mapbox's native scroll zoom so the wheel
  // doesn't fire `zoomstart` (which drops follow / "breaks the link"). The custom
  // wheel handler zooms the chase-cam toward/away from the drone instead, keeping
  // the link. When follow is broken (free map), native scroll zoom is restored.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (cameraFollowing) {
      map.scrollZoom.disable();
    } else {
      map.scrollZoom.enable();
    }
    map.dragRotate.enable();
    if (map.touchZoomRotate) map.touchZoomRotate.enable();
  }, [cameraFollowing]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (appliedStyleUrlRef.current === styleUrl) return;

    appliedStyleUrlRef.current = styleUrl;
    try {
      map.setStyle(styleUrl);
    } catch (err) {
      console.warn('Failed to apply 3D map style:', err);
    }
  }, [styleUrl]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;

    ensureTerrainAndBuildings(map, show3DBuildings);
    map.triggerRepaint();
  }, [show3DBuildings]);

  // ── No-fly zone / airspace overlay in 3D ──
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !geoZones) return;

    const renderZones = () => {
      if (!map.isStyleLoaded()) return;

      // Remove existing zone layers before re-adding (handles style reload)
      cleanupMapLayers(map, 'utms-zones');

      try {
        if (!map.getSource(ZONE_SOURCE_ID)) {
          map.addSource(ZONE_SOURCE_ID, { type: 'geojson', data: geoZones });
        } else {
          map.getSource(ZONE_SOURCE_ID).setData(geoZones);
        }

        const beforeId = map.getLayer(DRONE_MODEL_LAYER_ID) ? DRONE_MODEL_LAYER_ID : undefined;

        // 3D extruded columns — gives clear visual airspace pillars in 3D view
        if (!map.getLayer(ZONE_FILL_LAYER_ID)) {
          map.addLayer({
            id: ZONE_FILL_LAYER_ID,
            type: 'fill-extrusion',
            source: ZONE_SOURCE_ID,
            paint: {
              'fill-extrusion-color': ['coalesce', ['get', 'fill'], '#ff0000'],
              'fill-extrusion-opacity': 0.22,
              'fill-extrusion-height': ZONE_EXTRUSION_HEIGHT_METERS,
              'fill-extrusion-base': 0,
            },
          }, beforeId);
        }

        // Bright boundary outline on the ground
        if (!map.getLayer(ZONE_LINE_LAYER_ID)) {
          map.addLayer({
            id: ZONE_LINE_LAYER_ID,
            type: 'line',
            source: ZONE_SOURCE_ID,
            paint: {
              'line-color': ['coalesce', ['get', 'fill'], '#ff0000'],
              'line-width': 2.5,
              'line-opacity': 0.75,
            },
          }, beforeId);
        }

        // Apply the current zone-visibility filter (null = show all).
        const sel = selectedZonesRef.current;
        const zoneFilter = sel === null ? null : ['in', ['coalesce', ['get', 'zone'], ['get', 'name']], ['literal', sel]];
        if (map.getLayer(ZONE_FILL_LAYER_ID)) map.setFilter(ZONE_FILL_LAYER_ID, zoneFilter);
        if (map.getLayer(ZONE_LINE_LAYER_ID)) map.setFilter(ZONE_LINE_LAYER_ID, zoneFilter);

        ensureDroneLayerOnTop(map);
      } catch (err) {
        console.warn('Zone overlay error:', err);
      }
    };

    if (map.isStyleLoaded()) {
      renderZones();
    } else {
      map.once('style.load', renderZones);
    }

    // Re-apply zones after any style change (style reload wipes all layers)
    map.on('style.load', renderZones);
    return () => {
      map.off('style.load', renderZones);
    };
  }, [geoZones]);

  // Unique zone names available for filtering (mirrors the 2D map's logic).
  const availableZones = useMemo(() => {
    if (!geoZones?.features) return [];
    const set = new Set();
    geoZones.features.forEach((f) => {
      const name = f.properties?.zone || f.properties?.name;
      if (name) set.add(name);
    });
    return Array.from(set).sort();
  }, [geoZones]);

  // On first load, show all zones (preserve the user's choice afterwards).
  useEffect(() => {
    if (availableZones.length > 0) {
      setSelectedZones((prev) => (prev === null ? availableZones : prev));
    }
  }, [availableZones]);

  // Live-apply the zone filter to the 3D layers when the selection changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const zoneFilter = selectedZones === null ? null : ['in', ['coalesce', ['get', 'zone'], ['get', 'name']], ['literal', selectedZones]];
    if (map.getLayer(ZONE_FILL_LAYER_ID)) map.setFilter(ZONE_FILL_LAYER_ID, zoneFilter);
    if (map.getLayer(ZONE_LINE_LAYER_ID)) map.setFilter(ZONE_LINE_LAYER_ID, zoneFilter);
    if (map.triggerRepaint) map.triggerRepaint();
  }, [selectedZones]);

  const effectiveSelectedZones = selectedZones === null ? availableZones : selectedZones;

  const toggleZone = useCallback((zone) => {
    setSelectedZones((prev) => {
      const base = prev === null ? availableZones : prev;
      return base.includes(zone) ? base.filter((z) => z !== zone) : [...base, zone];
    });
  }, [availableZones]);

  const toggleAllZones = useCallback(() => {
    setSelectedZones((prev) => {
      const base = prev === null ? availableZones : prev;
      return base.length === availableZones.length ? [] : [...availableZones];
    });
  }, [availableZones]);

  // ── Active drone safety radius ──
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isActive) return;

    if (!showSafetyRadius) {
      const removeRadiusLayers = () => cleanupMapLayers(map, 'utms-drone-radius');
      if (map.isStyleLoaded()) {
        removeRadiusLayers();
      } else {
        map.once('style.load', removeRadiusLayers);
      }
      return;
    }

    if (!map.isStyleLoaded()) return;

    const pose = activePoseRef.current;
    if (!pose || !hasValidCoordinates(pose.lat, pose.lon)) {
      cleanupMapLayers(map, 'utms-drone-radius');
      return;
    }

    const circleFeature = buildCirclePolygon(pose.lat, pose.lon, DRONE_RADIUS_METERS, DRONE_RADIUS_STEPS);
    const circleGeoJson = {
      type: 'FeatureCollection',
      features: [circleFeature],
    };

    if (!map.getSource(DRONE_RADIUS_SOURCE_ID)) {
      map.addSource(DRONE_RADIUS_SOURCE_ID, { type: 'geojson', data: circleGeoJson });
      map.addLayer({
        id: DRONE_RADIUS_FILL_ID,
        type: 'fill',
        source: DRONE_RADIUS_SOURCE_ID,
        paint: {
          'fill-color': '#38bdf8',
          'fill-opacity': 0.12,
        },
      });
      map.addLayer({
        id: DRONE_RADIUS_LINE_ID,
        type: 'line',
        source: DRONE_RADIUS_SOURCE_ID,
        paint: {
          'line-color': '#38bdf8',
          'line-width': 2,
          'line-opacity': 0.55,
        },
      });
    } else {
      map.getSource(DRONE_RADIUS_SOURCE_ID).setData(circleGeoJson);
    }
  }, [activeDroneId, dronesTelemetry, isActive, showSafetyRadius]);

  // ── Mission route rendering (ground overlay + 3D teal corridor) ──
  // Map page: draws EVERY saved mission per drone as a floating corridor, with
  //   the active (flying) one highlighted.
  // Planner preview: when `localRoutes` is provided, draws those in-progress
  //   (unsaved) waypoints directly — no fetch — so the corridor updates live
  //   as the user edits.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const publishRoutes = (routes) => {
      routeDataRef.current = { version: routeDataRef.current.version + 1, routes };
      if (map.isStyleLoaded()) map.triggerRepaint();
    };

    if (!showMissionRoutes) {
      cleanupMapLayers(map, 'utms-mission-');
      publishRoutes([]);
      return;
    }

    let cancelled = false;

    // Gather { droneId, missionId, waypoints, active } for every corridor to draw.
    const gatherMissions = async () => {
      // Planner live preview: in-progress (unsaved) waypoints, no fetch.
      if (localRoutes) {
        return localRoutes.map((r) => ({
          droneId: r.id,
          missionId: r.missionId || r.id,
          waypoints: r.waypoints || [],
          active: !!r.active,
          home: r.home || null,
          rerouteTransition: r.rerouteTransition || null,
        }));
      }

      // Map page: every saved mission for active connected drones
      const activeDronesList = drones.filter((d) => {
        const tel = dronesTelemetryRef.current?.[d.id] || d.telemetry;
        const mode = String(tel?.mode || '').trim().toUpperCase();
        return !!tel?.connected && !tel?.stale && !!mode && mode !== 'N/A' && mode !== 'DISCONNECTED';
      });
      const lists = await Promise.all(activeDronesList.map(async (d) => {
        const am = activeMissionsRef.current?.[d.id] || null;
        const activeMissionId = am?.missionId || null;
        const isRunning = !!am && ACTIVE_MISSION_STATES.has(am.state);
        // Only the mission that is ACTUALLY FLYING gets drawn. Every saved
        // mission used to render as a dim preview too, so the sky filled with
        // corridors nobody was in — including plans belonging to a drone that
        // had already landed. A route on this map means live traffic.
        if (!activeMissionId || !isRunning) return [];
        try {
          const res = await apiFetch(`${API_URL}/drones/${d.id}/missions?limit=${ROUTE_MAX_MISSIONS_PER_DRONE}`);
          if (!res.ok) return [];
          const data = await res.json();
          const missions = (data?.missions || [])
            .slice()
            .sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0))
            .slice(0, ROUTE_MAX_MISSIONS_PER_DRONE);
          const running = missions.find((m) => m.id === activeMissionId);
          if (!running) return [];
          return [{
            droneId: d.id,
            missionId: running.id,
            waypoints: running.waypoints || [],
            active: true,
            startedAt: am?.startedAt || null,
            durationSec: am?.durationSec ?? null,
            home: d.homeLocation || null,
            rerouteTransition: am?.rerouteTransition || null,
          }];
        } catch {
          return [];
        }
      }));
      return lists.flat();
    };

    // If the style isn't ready, retry when the map settles ('idle' is
    // guaranteed to fire; 'style.load' only fires on style CHANGES, so bailing
    // without this rearm silently dropped the routes forever).
    const retryWhenReady = () => {
      map.off('idle', renderMissionPaths);
      map.once('idle', renderMissionPaths);
    };

    const renderMissionPaths = async () => {
      if (cancelled) return;
      if (!map.isStyleLoaded()) {
        retryWhenReady();
        return;
      }
      cleanupMapLayers(map, 'utms-mission-');

      if (!localRoutes && !drones.length) {
        publishRoutes([]);
        return;
      }

      const missions = await gatherMissions();
      if (cancelled) return;
      if (!map.isStyleLoaded()) {
        retryWhenReady();
        return;
      }

      const routes = [];
      const nextDeliveryDrops = {};
      const nextActiveWps = {};   // droneId -> waypoints of the running mission
      const activeCorridorInfo = [];  // for the allocated-time countdown overlay

      (missions || []).filter(Boolean).forEach((m) => {
        if (!m || !m.droneId) return;
        const { droneId, missionId, waypoints, active, home, startedAt, durationSec, rerouteTransition } = m;
        // Detect an active winch-delivery mission (has DO_WINCH legs) and record
        // its drop points so the 3D layer can lower a rope + parcel at each one.
        if (active) {
          nextActiveWps[droneId] = waypoints || [];
          const rawWps = waypoints || [];
          const payOutIndex = rawWps.findIndex((wp) => wp.type === 'DO_WINCH' && Number(wp.param3) > 0);
          const payOut = payOutIndex >= 0 ? rawWps[payOutIndex] : null;
          if (payOut) {
            const ropeLength = Math.abs(Number(payOut.param3)) || 15;
            // Play the rope at the pace the mission flies it: the winch travel is
            // rope ÷ rate, and the hook opens when the hold after the pay-out
            // ends. A mission whose hold is shorter than the travel (generated
            // before the winch wait was computed) keeps the short fixed animation.
            const winchRate = Number(payOut.param4);
            const travelMs = winchRate > 0 ? (ropeLength / winchRate) * 1000 : 0;
            const holdAfterPayOut = rawWps.slice(payOutIndex + 1).find((wp) => wp.type === 'LOITER_TIME');
            const releaseMs = holdAfterPayOut ? Number(holdAfterPayOut.param1) * 1000 : 0;
            const winchMs = travelMs > 0 && releaseMs >= travelMs
              ? { out: travelMs, hold: releaseMs - travelMs, in: travelMs }
              : { out: WINCH_OUT_MS, hold: WINCH_HOLD_MS, in: WINCH_IN_MS };
            const seen = new Set();
            const drops = [];
            rawWps.forEach((wp) => {
              if (wp.type !== 'WAYPOINT') return;
              const lat = Number(wp.latitude);
              const lon = Number(wp.longitude);
              if (!Number.isFinite(lat) || !Number.isFinite(lon) || (lat === 0 && lon === 0)) return;
              const key = `${lat.toFixed(6)},${lon.toFixed(6)}`;
              if (seen.has(key)) return;
              seen.add(key);
              drops.push({ lat, lon, key });
            });
            const wpAlts = rawWps.filter((wp) => wp.type === 'WAYPOINT').map((wp) => Number(wp.altitude)).filter((a) => a > 0);
            const dropHeight = wpAlts.length ? Math.min(...wpAlts) : 15;
            if (drops.length) nextDeliveryDrops[droneId] = { missionId, ropeLength, dropHeight, drops, winchMs };
          }
        }

        const allWps = (waypoints || []).filter(
          (wp) => !NON_LOCATION_COMMANDS.has(wp.type) && !(wp.latitude === 0 && wp.longitude === 0)
        );
        if (allWps.length === 0) return;

        // Waypoint markers: the mission's own points (TAKEOFF's click position
        // is not where the copter climbs, so it gets no marker).
        const wps = allWps.filter((wp) => wp.type !== 'HOME' && wp.type !== 'TAKEOFF');
        if (wps.length < 1) return;

        // The path the copter flies — the same one the server's corridor
        // monitor checks (utils/flightPath.js): climb at home, the waypoints,
        // LAND as a level approach then straight down, RTL as a leg back over
        // home then down. A home implausibly far from the mission (stale or
        // wrong) is ignored, so it cannot draw a leg across the whole map.
        const homeLat = Number(home?.lat);
        const homeLon = Number(home?.lon);
        const homeOk = hasValidCoordinates(homeLat, homeLon) && Math.hypot(
          (homeLat - Number(wps[0].latitude)) * 111320,
          (homeLon - Number(wps[0].longitude)) * 111320 * Math.cos((homeLat * Math.PI) / 180)
        ) < ROUTE_HOME_MAX_DISTANCE_METERS;
        const points = flownPath((waypoints || []).filter((wp) => wp && wp.type !== 'HOME'), homeOk ? { lat: homeLat, lon: homeLon } : null)
          .filter((p) => p && !NON_LOCATION_COMMANDS.has(p.type))
          .map((p) => ({
            lat: Number(p.lat ?? p.latitude),
            lon: Number(p.lon ?? p.longitude),
            alt: Number(p.alt ?? p.altitude),
            frame: Number(p.frame),
            type: p.type,
            synthetic: ADDED_POINT_TYPES.has(p.type), // no waypoint sphere
          }))
          .filter((p) => hasValidCoordinates(p.lat, p.lon));
        if (points.length < 1) return;

        const key = `${droneId}-${missionId}`;
        const srcId = `utms-mission-${key}`;
        const lineId = `utms-mission-line-${key}`;
        const wpSrcId = `utms-mission-wp-${key}`;
        const wpId = `utms-mission-pt-${key}`;

        // Ground shadow of the same path (from home, back home after RTL).
        const lineCoords = [];
        points.forEach((p) => {
          const last = lineCoords[lineCoords.length - 1];
          if (!last || Math.abs(p.lon - last[0]) > 1e-7 || Math.abs(p.lat - last[1]) > 1e-7) {
            lineCoords.push([p.lon, p.lat]);
          }
        });
        const lineGeoJson = {
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: lineCoords },
        };
        const pointGeoJson = {
          type: 'FeatureCollection',
          features: wps.map((wp, i) => ({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [wp.longitude, wp.latitude] },
            properties: { index: i + 1, type: wp.type },
          })),
        };

        try {
          // Faint teal ground-shadow + nodes beneath the floating corridor.
          if (!map.getSource(srcId)) {
            map.addSource(srcId, { type: 'geojson', data: lineGeoJson });
            map.addLayer({ id: lineId, type: 'line', source: srcId, paint: { 'line-color': ROUTE_CORRIDOR_COLOR, 'line-width': 2, 'line-opacity': active ? 0.5 : 0.3, 'line-dasharray': [2, 2] } });
          } else {
            map.getSource(srcId).setData(lineGeoJson);
          }
          if (!map.getSource(wpSrcId)) {
            map.addSource(wpSrcId, { type: 'geojson', data: pointGeoJson });
            map.addLayer({ id: wpId, type: 'circle', source: wpSrcId, paint: { 'circle-radius': 4, 'circle-color': ROUTE_CORRIDOR_EDGE_COLOR, 'circle-stroke-width': 1.5, 'circle-stroke-color': '#0b3b36', 'circle-opacity': active ? 0.7 : 0.4 } });
          } else {
            map.getSource(wpSrcId).setData(pointGeoJson);
          }
        } catch (err) {
          console.warn('Mission layer error:', err);
        }

        // Countdown overlay window for a live corridor. Timing is SERVER-owned:
        // `startedAt` is the backend's mission start (authoritative even if the map
        // opened mid-flight) and `durationSec` is the backend's single estimate.
        // Buffer is a display-only pad from ops config. No client-side duration
        // re-implementation (that drifted from the backend). If the server hasn't
        // supplied a duration yet (brief window at launch), skip the countdown.
        const serverDuration = Number(durationSec);
        if (active && missionId && Number.isFinite(serverDuration)) {
          const start = startedAt ? new Date(startedAt).getTime() : Date.now();
          const bufferSec = (configRef.current && configRef.current.corridor && Number.isFinite(configRef.current.corridor.timeBufferSeconds)) ? configRef.current.corridor.timeBufferSeconds : 30;
          const end = start + (serverDuration + bufferSec) * 1000;
          activeCorridorInfo.push({ droneId, missionId, start, end, durationSec: serverDuration });
        }

        routes.push({ droneId, missionId, active, points });

        // Add temporary reroute transition tunnel if mid-flight route change is active
        if (active && rerouteTransition && rerouteTransition.start && rerouteTransition.target) {
          const tStart = rerouteTransition.start;
          const tTarget = rerouteTransition.target;
          if (hasValidCoordinates(Number(tStart.lat), Number(tStart.lon)) && hasValidCoordinates(Number(tTarget.lat), Number(tTarget.lon))) {
            const distM = Math.hypot(
              (Number(tStart.lat) - Number(tTarget.lat)) * 111320,
              (Number(tStart.lon) - Number(tTarget.lon)) * 111320 * Math.cos((Number(tStart.lat) * Math.PI) / 180)
            );
            if (distM > 2) {
              const transPoints = [
                { lat: Number(tStart.lat), lon: Number(tStart.lon), alt: Number(tStart.alt) || 50, frame: 3, type: 'TRANSITION' },
                { lat: Number(tTarget.lat), lon: Number(tTarget.lon), alt: Number(tTarget.alt) || 50, frame: 3, type: 'TRANSITION' },
              ];
              const tKey = `${droneId}-transition`;
              const tSrcId = `utms-mission-${tKey}`;
              const tLineId = `utms-mission-line-${tKey}`;
              try {
                const transLineGeoJson = {
                  type: 'Feature',
                  geometry: { type: 'LineString', coordinates: [[Number(tStart.lon), Number(tStart.lat)], [Number(tTarget.lon), Number(tTarget.lat)]] }
                };
                if (!map.getSource(tSrcId)) {
                  map.addSource(tSrcId, { type: 'geojson', data: transLineGeoJson });
                  map.addLayer({
                    id: tLineId,
                    type: 'line',
                    source: tSrcId,
                    paint: {
                      'line-color': ROUTE_TRANSITION_COLOR,
                      'line-width': 3,
                      'line-opacity': 0.85,
                      'line-dasharray': [3, 3]
                    }
                  });
                } else {
                  map.getSource(tSrcId).setData(transLineGeoJson);
                }
              } catch (e) {
                // non-blocking
              }

              routes.push({
                droneId: tKey,
                missionId: `reroute-${missionId || 'temp'}`,
                active: true,
                isTransition: true,
                color: ROUTE_TRANSITION_COLOR,
                points: transPoints,
              });
            }
          }
        }
      });

      setCorridorInfo(activeCorridorInfo);

      deliveryDropsRef.current = nextDeliveryDrops;
      setActiveMissionWps(nextActiveWps);
      addRouteRibbonLayer(map);
      publishRoutes(routes);
      ensureDroneLayerOnTop(map);
    };

    renderMissionPaths();
    map.on('style.load', renderMissionPaths);
    return () => {
      cancelled = true;
      map.off('style.load', renderMissionPaths);
      map.off('idle', renderMissionPaths);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drones, activeMissionIdsKey, missionRefreshTick, showMissionRoutes, addRouteRibbonLayer, localRoutes]);

  // ── Flight trail breadcrumb ──
  useEffect(() => {
    if (!activeDroneId) return;
    const telemetry = dronesTelemetry[activeDroneId];
    const lat = Number(telemetry?.lat);
    const lon = Number(telemetry?.lon);
    if (!hasValidCoordinates(lat, lon)) return;

    if (!flightTrailRef.current[activeDroneId]) {
      flightTrailRef.current[activeDroneId] = [];
    }
    const trail = flightTrailRef.current[activeDroneId];
    const last = trail[trail.length - 1];
    if (!last || Math.abs(lat - last[1]) > 0.000005 || Math.abs(lon - last[0]) > 0.000005) {
      trail.push([lon, lat]);
      if (trail.length > MAX_TRAIL_POINTS) trail.shift();
    }

    const map = mapRef.current;
    if (!map || !map.isStyleLoaded() || trail.length < 2) return;

    const trailGeoJson = { type: 'Feature', geometry: { type: 'LineString', coordinates: trail } };
    try {
      if (map.getSource('utms-flight-trail')) {
        map.getSource('utms-flight-trail').setData(trailGeoJson);
      } else {
        map.addSource('utms-flight-trail', { type: 'geojson', data: trailGeoJson });
        map.addLayer({ id: 'utms-flight-trail-line', type: 'line', source: 'utms-flight-trail', paint: { 'line-color': '#38bdf8', 'line-width': 2.5, 'line-opacity': 0.75 } });
        ensureDroneLayerOnTop(map);
      }
    } catch { /* style transition race */ }
  }, [activeDroneId, dronesTelemetry]);

  // ── 3D flown trail ── where each of the operator's drones has actually flown
  // during its current mission, drawn at its real height inside the planned
  // corridor (createRouteRibbonLayer). One point per metre moved; the trail is
  // dropped when the flight ends or another mission starts.
  useEffect(() => {
    const trails = trailsRef.current;
    let changed = false;
    Object.keys(trails.byDrone).forEach((id) => {
      const am = activeMissions && activeMissions[id];
      if (!am || !ACTIVE_MISSION_STATES.has(am.state) || am.missionId !== trails.byDrone[id].missionId) {
        delete trails.byDrone[id];
        changed = true;
      }
    });
    Object.entries(activeMissions || {}).forEach(([id, am]) => {
      if (!am || !ACTIVE_MISSION_STATES.has(am.state)) return;
      const tel = dronesTelemetry && dronesTelemetry[id];
      const lat = Number(tel && tel.lat);
      const lon = Number(tel && tel.lon);
      // Height above home, like the route's waypoints.
      const agl = Number(tel && (Number.isFinite(Number(tel.relative_alt)) ? tel.relative_alt : tel.alt));
      if (!hasValidCoordinates(lat, lon) || !Number.isFinite(agl)) return;
      const trail = trails.byDrone[id] || (trails.byDrone[id] = { missionId: am.missionId, pts: [] });
      const last = trail.pts[trail.pts.length - 1];
      const movedM = last
        ? Math.hypot((lat - last.lat) * 111320, (lon - last.lon) * 111320 * Math.cos((lat * Math.PI) / 180), agl - last.agl)
        : Infinity;
      if (movedM < 1) return;
      trail.pts.push({ lat, lon, agl: Math.max(0, agl) });
      if (trail.pts.length > MAX_FLOWN_TRAIL_POINTS) trail.pts.shift();
      changed = true;
    });
    if (changed) {
      trails.version += 1;
      const map = mapRef.current;
      if (map && map.isStyleLoaded()) map.triggerRepaint();
    }
  }, [dronesTelemetry, activeMissions]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isActive || requiredModelUrls.size === 0) {
      return;
    }
    // addDroneModelLayer is internally guarded (dedupe + try/catch), so call it
    // directly — gating on isStyleLoaded() here silently dropped the layer
    // whenever tiles were still streaming in ('style.load' never re-fires).
    addDroneModelLayer(map);
  }, [requiredModelUrls, addDroneModelLayer, isActive]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // Drones render as THREE.js GLB models in the custom layer; the flat SVG
    // markers are a debug overlay only. Selection happens via the sidebar/dropdown.
    if (!show2DMarkers) {
      Object.values(markersRef.current).forEach((marker) => marker.remove());
      markersRef.current = {};
      return;
    }

    const nextMarkerIds = new Set();

    trackedDrones.forEach((drone) => {
      const telemetry = dronesTelemetry[drone.id] || drone.telemetry;
      const pose = deriveDronePose(drone.id, telemetry);
      if (!pose) {
        return;
      }

      const isReadonly = !!drone.readonly;
      const isSelected = drone.id === activeDroneId;
      // An off-nominal airspace claim outranks selection and ownership styling:
      // a drone outside its declared corridor must not be able to hide behind
      // "not currently selected". Same palette as the 2D map — see
      // utils/intentState.js, which exists so the two cannot drift apart.
      const intentColor = intentMarkerColor(telemetry?.intentState);
      const markerColor = intentColor || (isReadonly ? '#94a3b8' : (isSelected ? '#f59e0b' : '#22d3ee'));

      nextMarkerIds.add(drone.id);

      let marker = markersRef.current[drone.id];

      const iconHtml = getDroneMarkerSVG({
        size: 48,
        color: markerColor,
        heading: pose.heading,
        isSelected,
        isConnected: !!telemetry?.connected,
        label: (drone.name || 'D').charAt(0).toUpperCase(),
        id: drone.id,
      });

      if (!marker) {
        const element = document.createElement('div');
        element.className = 'utms-3d-drone-marker';
        element.style.width = '48px';
        element.style.height = '48px';
        element.style.cursor = isReadonly ? 'default' : 'pointer';
        element.style.userSelect = 'none';
        element.innerHTML = iconHtml;

        if (!isReadonly) {
          element.addEventListener('click', () => {
            if (onFollowDroneChange) {
              onFollowDroneChange(drone.id);
            }
            if (onDroneSelect) {
              onDroneSelect(drone);
            }
          });
        }

        marker = new mapboxgl.Marker({ element, anchor: 'center' })
          .setLngLat([pose.lon, pose.lat])
          .addTo(map);
        markersRef.current[drone.id] = marker;
      } else {
        marker.setLngLat([pose.lon, pose.lat]);
        const element = marker.getElement();
        element.style.cursor = isReadonly ? 'default' : 'pointer';
        element.innerHTML = iconHtml;
      }
    });

    Object.keys(markersRef.current).forEach((droneId) => {
      if (!nextMarkerIds.has(droneId)) {
        markersRef.current[droneId].remove();
        delete markersRef.current[droneId];
      }
    });
  }, [trackedDrones, dronesTelemetry, activeDroneId, deriveDronePose, onDroneSelect, onFollowDroneChange, show2DMarkers]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isActive) {
      return;
    }

    map.resize();

    if (requiredModelUrls.size > 0) {
      // Internally guarded — see addDroneModelLayer; isStyleLoaded() gating
      // here silently dropped the layer during tile streaming.
      addDroneModelLayer(map);
    }

    map.triggerRepaint();
  }, [isActive, requiredModelUrls, addDroneModelLayer]);

  const resolveCameraRig = useCallback((mode, pose, altitudeMeters, smoothedHeading) => {
    const heading = Number.isFinite(smoothedHeading) ? smoothedHeading : (Number.isFinite(pose.heading) ? pose.heading : 0);
    const zoomFactor = userZoomFactorRef.current;
    const orbitOffset = userOrbitOffsetRef.current;

    if (mode === 'first_person') {
      // True nose-cam: sit just AHEAD of the airframe (past the ~1.5m model so the
      // drone's own body stays out of frame) at roughly nose height, looking forward
      // along the heading with a gentle downward tilt so the ground/route ahead fills
      // the view instead of the drone itself. Fixed offsets keep the FPV steady.
      const cameraPoint = offsetLatLon(pose.lat, pose.lon, heading, FIRST_PERSON_FORWARD_DISTANCE_METERS);
      const lookPoint = offsetLatLon(pose.lat, pose.lon, heading, FIRST_PERSON_LOOKAHEAD_METERS);
      return {
        camera: { lat: cameraPoint.lat, lon: cameraPoint.lon, alt: altitudeMeters + FIRST_PERSON_UP_DISTANCE_METERS },
        look: { lat: lookPoint.lat, lon: lookPoint.lon, alt: altitudeMeters - FIRST_PERSON_LOOK_DROP_METERS },
      };
    }

    if (mode === 'second_person') {
      const cameraPoint = offsetLatLon(pose.lat, pose.lon, (heading + orbitOffset + 360) % 360, SECOND_PERSON_AHEAD_DISTANCE_METERS * zoomFactor);
      return {
        camera: { lat: cameraPoint.lat, lon: cameraPoint.lon, alt: altitudeMeters + SECOND_PERSON_UP_DISTANCE_METERS * zoomFactor },
        look: { lat: pose.lat, lon: pose.lon, alt: altitudeMeters },
      };
    }

    // 3rd person: camera behind + above, looking AT the drone so it stays pinned to
    // screen centre. Orbit offset rotates the camera AROUND the drone (drone stays
    // centred); zoom scales the chase distance. Looking at the drone itself (not a
    // point ahead) keeps camera and look on the same axis, so orbit can't decentre it.
    const behindBearing = (heading + 180 + orbitOffset + 360) % 360;
    const cameraPoint = offsetLatLon(pose.lat, pose.lon, behindBearing, THIRD_PERSON_BACK_DISTANCE_METERS * zoomFactor);
    return {
      camera: { lat: cameraPoint.lat, lon: cameraPoint.lon, alt: altitudeMeters + THIRD_PERSON_UP_DISTANCE_METERS * zoomFactor },
      look: { lat: pose.lat, lon: pose.lon, alt: altitudeMeters },
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const pose = activePoseRef.current;
    if (!map || !pose || !isActive || !cameraFollowing) {
      // Cancel any running animation frame when not following
      if (cameraAnimFrameRef.current) {
        cancelAnimationFrame(cameraAnimFrameRef.current);
        cameraAnimFrameRef.current = null;
      }
      return;
    }

    const POS_LERP = 0.08;      // Position interpolation — snappier tracking
    const HEADING_LERP = 0.045;  // Heading interpolation — cinematic, smooth turn lag
    const SKIP_THRESHOLD = 1e-12; // Skip frame if camera hasn't moved meaningfully

    const lerp = (a, b, t) => a + (b - a) * t;

    // Shortest-arc angle interpolation (handles 350° → 10° correctly)
    const lerpAngle = (from, to, t) => {
      let diff = ((to - from + 540) % 360) - 180; // Normalize to [-180, 180]
      return ((from + diff * t) % 360 + 360) % 360; // Keep in [0, 360)
    };

    const animateCamera = () => {
      const currentPose = activePoseRef.current;
      const currentMap = mapRef.current;
      if (!currentMap || !currentPose || !hasValidCoordinates(currentPose.lat, currentPose.lon)) {
        cameraAnimFrameRef.current = requestAnimationFrame(animateCamera);
        return;
      }

      // Smooth the heading independently using angle-aware interpolation
      const rawHeading = Number.isFinite(currentPose.heading) ? currentPose.heading : 0;
      if (smoothCameraHeadingRef.current === null) {
        smoothCameraHeadingRef.current = rawHeading;
      }
      smoothCameraHeadingRef.current = lerpAngle(smoothCameraHeadingRef.current, rawHeading, HEADING_LERP);

      const altitudeMeters = resolveModelAltitudeMeters(currentMap, currentPose, activeDroneId, activeRoutesRef) + DRONE_ALTITUDE_OFFSET_METERS;
      const rig = resolveCameraRig(cameraMode, currentPose, altitudeMeters, smoothCameraHeadingRef.current);

      const targetCamPos = mapboxgl.MercatorCoordinate.fromLngLat(
        [rig.camera.lon, rig.camera.lat],
        rig.camera.alt
      );
      const targetLookPos = mapboxgl.MercatorCoordinate.fromLngLat(
        [rig.look.lon, rig.look.lat],
        rig.look.alt
      );

      // Initialize smooth positions on first frame
      if (!smoothCamPosRef.current) {
        smoothCamPosRef.current = { x: targetCamPos.x, y: targetCamPos.y, z: targetCamPos.z };
      }
      if (!smoothCamLookRef.current) {
        smoothCamLookRef.current = { x: targetLookPos.x, y: targetLookPos.y, z: targetLookPos.z };
      }
      if (smoothLookAltRef.current === null) {
        smoothLookAltRef.current = rig.look.alt;
      }

      // Smooth lerp towards target
      smoothCamPosRef.current.x = lerp(smoothCamPosRef.current.x, targetCamPos.x, POS_LERP);
      smoothCamPosRef.current.y = lerp(smoothCamPosRef.current.y, targetCamPos.y, POS_LERP);
      smoothCamPosRef.current.z = lerp(smoothCamPosRef.current.z, targetCamPos.z, POS_LERP);

      smoothCamLookRef.current.x = lerp(smoothCamLookRef.current.x, targetLookPos.x, POS_LERP);
      smoothCamLookRef.current.y = lerp(smoothCamLookRef.current.y, targetLookPos.y, POS_LERP);
      smoothCamLookRef.current.z = lerp(smoothCamLookRef.current.z, targetLookPos.z, POS_LERP);

      // Smooth the look-at altitude directly in meters (avoids unreliable toAltitude())
      smoothLookAltRef.current = lerp(smoothLookAltRef.current, rig.look.alt, POS_LERP);

      // Skip frame if smoothed values haven't changed meaningfully (saves GPU cycles)
      const camDelta = Math.abs(smoothCamPosRef.current.x - targetCamPos.x)
        + Math.abs(smoothCamPosRef.current.y - targetCamPos.y)
        + Math.abs(smoothCamPosRef.current.z - targetCamPos.z)
        + Math.abs(smoothCamLookRef.current.x - targetLookPos.x)
        + Math.abs(smoothCamLookRef.current.y - targetLookPos.y);
      if (camDelta < SKIP_THRESHOLD) {
        cameraAnimFrameRef.current = requestAnimationFrame(animateCamera);
        return;
      }

      // Convert smoothed mercator look position back to lng/lat for lookAtPoint
      const smoothLookMerc = new mapboxgl.MercatorCoordinate(
        smoothCamLookRef.current.x,
        smoothCamLookRef.current.y,
        smoothCamLookRef.current.z
      );
      const smoothLookLngLat = smoothLookMerc.toLngLat();

      const camera = currentMap.getFreeCameraOptions();
      camera.position = new mapboxgl.MercatorCoordinate(
        smoothCamPosRef.current.x,
        smoothCamPosRef.current.y,
        smoothCamPosRef.current.z
      );
      camera.lookAtPoint([smoothLookLngLat.lng, smoothLookLngLat.lat], undefined, smoothLookAltRef.current);
      currentMap.setFreeCameraOptions(camera);

      cameraAnimFrameRef.current = requestAnimationFrame(animateCamera);
    };

    // Reset smooth heading + positions when re-engaging or switching mode
    const initHeading = Number.isFinite(pose.heading) ? pose.heading : 0;
    smoothCameraHeadingRef.current = initHeading;
    const altitudeMeters = resolveModelAltitudeMeters(map, pose, activeDroneId, activeRoutesRef) + DRONE_ALTITUDE_OFFSET_METERS;
    const initialRig = resolveCameraRig(cameraMode, pose, altitudeMeters, initHeading);
    const initCam = mapboxgl.MercatorCoordinate.fromLngLat(
      [initialRig.camera.lon, initialRig.camera.lat],
      initialRig.camera.alt
    );
    const initLook = mapboxgl.MercatorCoordinate.fromLngLat(
      [initialRig.look.lon, initialRig.look.lat],
      initialRig.look.alt
    );
    smoothCamPosRef.current = { x: initCam.x, y: initCam.y, z: initCam.z };
    smoothCamLookRef.current = { x: initLook.x, y: initLook.y, z: initLook.z };
    smoothLookAltRef.current = initialRig.look.alt;

    cameraAnimFrameRef.current = requestAnimationFrame(animateCamera);

    return () => {
      if (cameraAnimFrameRef.current) {
        cancelAnimationFrame(cameraAnimFrameRef.current);
        cameraAnimFrameRef.current = null;
      }
    };
  }, [activeDroneId, cameraMode, isActive, cameraFollowing, resolveCameraRig]);

  // Picking a camera view (1st/2nd/3rd person) is an explicit "focus the drone"
  // action. A prior manual pan/zoom sets cameraFollowing=false and nothing turns
  // it back on, so without this the chase cam stays wherever the user left it and
  // the drone drifts off-centre. Re-engage follow on every mode switch. (No-op on
  // mount: cameraFollowing already starts true, so setState bails.)
  useEffect(() => {
    setCameraFollowing(true);
  }, [cameraMode]);

  const handleRecenterToDrone = useCallback(() => {
    const map = mapRef.current;
    const pose = activePoseRef.current;
    if (!map || !pose) {
      return;
    }
    // Clear any pending re-engage timer since user explicitly clicked recenter
    if (cameraReengageTimerRef.current) {
      clearTimeout(cameraReengageTimerRef.current);
      cameraReengageTimerRef.current = null;
    }
    // Reset smooth heading + cam positions to snap to current rig immediately
    // Also reset user zoom/orbit adjustments
    userZoomFactorRef.current = 1.0;
    userOrbitOffsetRef.current = 0;
    const initHeading = Number.isFinite(pose.heading) ? pose.heading : 0;
    smoothCameraHeadingRef.current = initHeading;
    const altitudeMeters = resolveModelAltitudeMeters(map, pose, activeDroneId, activeRoutesRef) + DRONE_ALTITUDE_OFFSET_METERS;
    const rig = resolveCameraRig(cameraMode, pose, altitudeMeters, initHeading);
    const camMerc = mapboxgl.MercatorCoordinate.fromLngLat(
      [rig.camera.lon, rig.camera.lat],
      rig.camera.alt
    );
    const lookMerc = mapboxgl.MercatorCoordinate.fromLngLat(
      [rig.look.lon, rig.look.lat],
      rig.look.alt
    );
    smoothCamPosRef.current = { x: camMerc.x, y: camMerc.y, z: camMerc.z };
    smoothCamLookRef.current = { x: lookMerc.x, y: lookMerc.y, z: lookMerc.z };
    smoothLookAltRef.current = rig.look.alt;
    setCameraFollowing(true);
  }, [cameraMode, resolveCameraRig, activeDroneId]);

  // "us drone pe focus kar" — the spoken form of the recenter button, so it runs
  // exactly the same camera snap rather than a second, subtly different one.
  useEffect(() => onDroneFocus((droneId) => {
    if (!droneId || droneId !== activeDroneId) return;
    handleRecenterToDrone();
  }), [activeDroneId, handleRecenterToDrone]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim() || !MAPBOX_TOKEN) return;
    try {
      const resp = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(searchQuery)}.json?access_token=${MAPBOX_TOKEN}&limit=1`);
      if (!resp.ok) throw new Error('Geocoding failed');
      const data = await resp.json();
      if (data.features && data.features.length > 0) {
        const [lng, lat] = data.features[0].center;
        const map = mapRef.current;
        if (map) {
          setCameraFollowing(false);
          runProgrammaticEase(map, {
            center: [lng, lat],
            zoom: 14,
            essential: true,
            duration: 2000
          });
        }
      }
    } catch (err) {
      console.error('Search error:', err);
    }
  };

  if (!MAPBOX_TOKEN) {
    return (
      <div style={OVERLAY_STYLES.missingToken}>
        Mapbox token missing. Configure REACT_APP_MAPBOX_TOKEN to enable 3D map mode.
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', height: '100%', width: '100%', pointerEvents: isActive ? 'auto' : 'none' }}>
      {/* 3D Search Bar — hidden when the Stitch overlay chrome provides it */}
      {!stitchChrome && (
      <form
        onSubmit={handleSearch}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: isMobileLayout ? 'calc(100% - 52px)' : '19rem',
          height: '50px',
          background: 'var(--bgSecondary)',
          zIndex: 900,
          display: 'flex',
          alignItems: 'center',
          boxSizing: 'border-box',
          borderBottom: '1px solid var(--borderPrimary)',
          transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search location..."
          style={{
            flex: 1,
            border: 'none',
            outline: 'none',
            fontSize: '14px',
            backgroundColor: "transparent",
            fontWeight: '500',
            color: 'var(--textPrimary)',
            padding: '0 16px',
            margin: 0,
            height: '100%',
            boxSizing: 'border-box'
          }}
        />
        <button
          type="submit"
          style={{
            background: "transparent",
            width: "40px",
            height: "40px",
            border: "none",
            borderRadius: "0",
            cursor: "pointer",
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--accent)',
            padding: 0,
            margin: '0 8px 0 0',
            transition: 'color 0.2s'
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--accentSecondary)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--accent)'; }}
        >
          <svg style={{ width: '20px', height: '20px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </button>
      </form>
      )}

      {/* 3D Zone (geofence) filter — hidden when the Stitch overlay chrome provides it */}
      {!stitchChrome && availableZones.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: `${topOffset + 62}px`,
            right: '260px',
            zIndex: 901,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: '6px',
            pointerEvents: 'auto',
          }}
        >
          <button
            type="button"
            onClick={() => setZoneFilterOpen((o) => !o)}
            title="Filter airspace zones"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: 'var(--bgSecondary)',
              border: '1px solid var(--borderPrimary)',
              borderRadius: '8px',
              padding: '7px 11px',
              fontSize: '12px',
              fontWeight: 700,
              color: zoneFilterOpen ? 'var(--accent)' : 'var(--textPrimary)',
              cursor: 'pointer',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
            }}
          >
            <svg style={{ width: '15px', height: '15px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L14 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 018 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
            </svg>
            Zones ({effectiveSelectedZones.length}/{availableZones.length})
          </button>

          {zoneFilterOpen && (
            <div
              style={{
                background: 'var(--bgSecondary)',
                border: '1px solid var(--borderPrimary)',
                borderRadius: '10px',
                padding: '8px',
                width: '230px',
                maxHeight: '320px',
                overflowY: 'auto',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                boxShadow: '0 8px 28px rgba(0,0,0,0.35)',
              }}
            >
              <button
                type="button"
                onClick={toggleAllZones}
                style={{
                  width: '100%',
                  background: 'transparent',
                  border: '1px solid var(--borderSecondary)',
                  borderRadius: '6px',
                  padding: '6px',
                  marginBottom: '6px',
                  fontSize: '11px',
                  fontWeight: 700,
                  color: 'var(--accent)',
                  cursor: 'pointer',
                  textTransform: 'uppercase',
                  letterSpacing: '0.02em',
                }}
              >
                {effectiveSelectedZones.length === availableZones.length ? 'Deselect All' : 'Select All'}
              </button>

              {availableZones.map((zone) => {
                const color = geoZones?.features?.find(
                  (f) => (f.properties?.zone || f.properties?.name) === zone
                )?.properties?.fill || '#ff0000';
                const checked = effectiveSelectedZones.includes(zone);
                return (
                  <label
                    key={zone}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '5px 4px',
                      fontSize: '12px',
                      color: checked ? 'var(--textPrimary)' : 'var(--textSecondary)',
                      cursor: 'pointer',
                      borderRadius: '4px',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleZone(zone)}
                      style={{ accentColor: 'var(--accent)', width: '14px', height: '14px', flexShrink: 0 }}
                    />
                    <span style={{ width: '11px', height: '11px', borderRadius: '2px', background: color, flexShrink: 0, border: '1px solid rgba(0,0,0,0.3)' }} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{zone}</span>
                  </label>
                );
              })}

              <div style={{ fontSize: '10px', color: 'var(--textSecondary)', textAlign: 'center', marginTop: '6px', opacity: 0.7 }}>
                {effectiveSelectedZones.length} of {availableZones.length} shown
              </div>
            </div>
          )}
        </div>
      )}

      {/* 3D Zoom Controls — hidden when the Stitch chrome + Mapbox bottom-right nav provide zoom */}
      {!stitchChrome && (
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: isMobileLayout ? 'auto' : '19rem',
          right: isMobileLayout ? '0px' : 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '0',
          height: '50px',
          width: '40px',
          zIndex: 900,
          transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          boxSizing: 'border-box'
        }}
      >
        <button
          onClick={() => {
            if (cameraFollowing && activeDroneId) {
              userZoomFactorRef.current = Math.max(0.12, userZoomFactorRef.current * 0.85);
            } else if (mapRef.current) {
              mapRef.current.zoomIn();
            }
          }}
          style={{
            width: '100%',
            height: '50%',
            background: 'var(--bgPrimary)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            border: 'none',
            borderBottom: '1px solid var(--borderSecondary)',
            borderRadius: '0',
            color: 'var(--accent)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '18px',
            fontWeight: '600',
            transition: 'background-color 0.2s, color 0.2s',
            margin: 0,
            padding: 0,
            boxSizing: 'border-box'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'var(--bgSecondary)';
            e.currentTarget.style.color = 'var(--accentSecondary)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'var(--bgPrimary)';
            e.currentTarget.style.color = 'var(--accent)';
          }}
          aria-label="Zoom in"
        >
          +
        </button>
        <button
          onClick={() => {
            if (cameraFollowing && activeDroneId) {
              userZoomFactorRef.current = Math.min(10.0, userZoomFactorRef.current * 1.15);
            } else if (mapRef.current) {
              mapRef.current.zoomOut();
            }
          }}
          style={{
            width: '100%',
            height: '50%',
            background: 'var(--bgPrimary)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            border: 'none',
            borderRadius: '0',
            color: 'var(--accent)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '18px',
            fontWeight: '600',
            transition: 'background-color 0.2s, color 0.2s',
            margin: 0,
            padding: 0,
            boxSizing: 'border-box'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'var(--bgSecondary)';
            e.currentTarget.style.color = 'var(--accentSecondary)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'var(--bgPrimary)';
            e.currentTarget.style.color = 'var(--accent)';
          }}
          aria-label="Zoom out"
        >
          −
        </button>
      </div>
      )}

      <div ref={containerRef} style={{ height: '100%', width: '100%' }} />
      {!stitchChrome && !activeDroneId && (
        <div style={OVERLAY_STYLES.statusBanner}>
          No active drone telemetry. 3D model will appear after valid lat/lon stream.
        </div>
      )}
      {activeDroneId && !modelReady && !modelFailed && (
        <div style={OVERLAY_STYLES.statusBanner}>
          Loading drone models...
        </div>
      )}
      {modelFailed && (
        <div style={OVERLAY_STYLES.errorBanner}>
          Drone model load interrupted. Retrying automatically...
        </div>
      )}
      {/* ── Active Missions & Corridors Status Cards ── */}
      {(missionPipeline.length > 0 || corridorInfo.length > 0) && (
        <div
          style={{
            position: 'absolute',
            left: 'var(--map-left-inset, 16px)',
            bottom: 140,
            zIndex: 25,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            width: 270,
            pointerEvents: 'none',
            transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          {/* Active Missions (with their embedded corridor countdown if present) */}
          {(missionPipeline || []).filter(Boolean).map((m) => {
            if (!m || !m.droneId) return null;
            const stalled = m.state !== 'running' && m.state !== 'taking_off';
            const accent = stalled ? '#fbbf24' : 'var(--accent, #00daf3)';
            const c = (corridorInfo || []).find((cor) => cor && cor.droneId === m.droneId);

            let corridorBlock = null;
            if (c) {
              const total = Math.max(1, c.end - c.start);
              const remainMs = c.end - Date.now();
              const frac = Math.max(0, Math.min(1, remainMs / total));
              const expired = remainMs <= 0;
              const secs = Math.max(0, Math.round(remainMs / 1000));
              const mmss = `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;
              const barColor = expired ? '#f87171' : frac < 0.25 ? '#fbbf24' : '#34d399';

              corridorBlock = (
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid color-mix(in srgb, var(--accent, #00daf3) 15%, transparent)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--stitch-outline, #849396)' }}>
                      ⏱ CORRIDOR TIMEOUT
                    </span>
                    <span style={{ fontSize: 10, fontWeight: 700, color: barColor }}>
                      {expired ? 'EXPIRED' : `${mmss} left`}
                    </span>
                  </div>
                  <div style={{ height: 3, borderRadius: 2, background: 'rgba(255, 255, 255, 0.1)', overflow: 'hidden' }}>
                    <div style={{ width: `${frac * 100}%`, height: '100%', borderRadius: 2, background: barColor, transition: 'width 1s linear' }} />
                  </div>
                </div>
              );
            }

            return (
              <div
                key={m.droneId}
                style={{
                  pointerEvents: 'auto',
                  padding: '10px 12px',
                  borderRadius: 12,
                  background: 'rgba(13, 17, 23, 0.88)',
                  backdropFilter: 'blur(16px) saturate(130%)',
                  WebkitBackdropFilter: 'blur(16px) saturate(130%)',
                  border: '1px solid color-mix(in srgb, var(--accent, #00daf3) 30%, transparent)',
                  boxShadow: '0 8px 32px -4px rgba(0, 0, 0, 0.65)',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--accent, #00daf3)' }}>
                    ◉ MISSION · {String(m.name).slice(0, 14)}
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: accent }}>{Math.round(m.progress)}%</span>
                </div>
                <div style={{ marginTop: 4, color: 'var(--textPrimary, #ffffff)', fontSize: 12, fontWeight: 700 }}>
                  {m.stage.label}
                  {stalled && <span style={{ color: accent, fontWeight: 600 }}> · {m.state.replace(/_/g, ' ')}</span>}
                </div>
                <div style={{ marginTop: 2, color: 'var(--stitch-outline, #849396)', fontSize: 10, fontWeight: 500 }}>
                  Step {m.stage.index + 1} of {m.stage.total}
                  {m.stage.index + 1 < m.stage.total && ` · next: ${m.stage.stages[m.stage.index + 1]}`}
                </div>
                {/* Pipeline as ticks */}
                <div style={{ marginTop: 6, display: 'flex', gap: 3 }}>
                  {m.stage.stages.map((label, i) => (
                    <div
                      key={`${label}-${i}`}
                      title={label}
                      style={{
                        flex: 1,
                        height: 3,
                        borderRadius: 2,
                        background: i < m.stage.index
                          ? 'color-mix(in srgb, var(--accent, #00daf3) 50%, transparent)'
                          : i === m.stage.index
                            ? accent
                            : 'rgba(255, 255, 255, 0.1)',
                        boxShadow: i === m.stage.index ? `0 0 6px ${accent}` : 'none',
                      }}
                    />
                  ))}
                </div>
                {corridorBlock}
              </div>
            );
          })}

          {/* Any standalone corridors that don't belong to an active mission pipeline */}
          {(corridorInfo || []).filter((c) => c && c.droneId && !missionPipeline.some((m) => m.droneId === c.droneId)).map((c) => {
            const total = Math.max(1, c.end - c.start);
            const remainMs = c.end - Date.now();
            const frac = Math.max(0, Math.min(1, remainMs / total));
            const expired = remainMs <= 0;
            const secs = Math.max(0, Math.round(remainMs / 1000));
            const mmss = `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;
            const name = ((drones || []).find((d) => d && d.id === c.droneId) || {}).name || c.droneId;
            const barColor = expired ? '#f87171' : frac < 0.25 ? '#fbbf24' : '#34d399';
            return (
              <div
                key={`${c.droneId}:${c.missionId}`}
                style={{
                  pointerEvents: 'auto',
                  padding: '9px 12px',
                  borderRadius: 12,
                  background: 'rgba(13, 17, 23, 0.88)',
                  backdropFilter: 'blur(16px) saturate(130%)',
                  WebkitBackdropFilter: 'blur(16px) saturate(130%)',
                  border: '1px solid color-mix(in srgb, var(--accent, #00daf3) 30%, transparent)',
                  boxShadow: '0 8px 32px -4px rgba(0, 0, 0, 0.65)',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--stitch-outline, #849396)' }}>
                    ⏱ CORRIDOR · {String(name).slice(0, 14)}
                  </span>
                  <span style={{ fontSize: 10, fontWeight: 700, color: barColor }}>{expired ? 'EXPIRED' : `${mmss} left`}</span>
                </div>
                <div style={{ marginTop: 6, height: 3, borderRadius: 2, background: 'rgba(255, 255, 255, 0.1)', overflow: 'hidden' }}>
                  <div style={{ width: `${frac * 100}%`, height: '100%', borderRadius: 2, background: barColor, transition: 'width 1s linear' }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default Map3DRenderer;
